<template>
  <div class="admin-page">
    <admin-page-header
      :eyebrow="pageEyebrow"
      :title="pageTitle"
      :description="pageDescription"
    />

    <v-card
      class="admin-table-card"
      outlined
      style="overflow: hidden;"
    >
      <v-tabs
        v-if="!carouselMode"
        v-model="campaignTab"
        class="campaign-view-tabs"
        show-arrows
        @change="onCampaignTabChange"
      >
        <v-tab>Kampanyalar</v-tab>
        <v-tab>
          Bitiş Kontrolü
          <v-chip
            x-small
            outlined
            color="warning"
            class="ml-2"
          >
            {{ expiryReviewTotal }}
          </v-chip>
        </v-tab>
        <v-tab v-if="canManageUrlHealth">
          Onay bekleyenler
        </v-tab>
      </v-tabs>

      <v-alert
        v-if="activationPending"
        dense
        outlined
        type="info"
        class="ma-4"
        role="status"
      >
        <strong>{{ activationPendingName }}</strong> aktifleştiriliyor.
        İşlem arka planda sürüyor; tamamlandığında liste yenilenecek.
      </v-alert>

      <v-alert
        v-if="campaignSaveSuccess"
        dense
        outlined
        type="success"
        class="ma-4"
        role="status"
      >
        {{ campaignSaveSuccess }}
      </v-alert>

      <campaign-candidate-queue
        v-if="candidateMode && canManageUrlHealth"
        ref="candidateQueue"
        @review="openCandidate"
        @published="loadTableData"
      />

      <div
        v-if="!expiryReviewMode && !candidateMode"
        class="admin-table-toolbar campaign-toolbar"
      >
        <div class="campaign-toolbar__filters">
          <v-text-field
            v-model="searchValue"
            append-icon="mdi-magnify"
            label="Adverport Kampanyalarını Ara"
            placeholder="Adverport Kampanyalarını adı ile ara..."
            single-line
            hide-details
            outlined
            dense
            class="search-field"
            clearable
            @input="onSearch"
          ></v-text-field>
          <v-select
            v-model="selectedStatusFilter"
            :items="statusOptions"
            item-text="text"
            item-value="value"
            label="Durum"
            hide-details
            outlined
            dense
            class="status-filter"
            @change="onStatusFilterChange"
          ></v-select>
          <v-select
            v-if="!carouselMode"
            v-model="selectedCarouselFilter"
            :items="carouselOptions"
            item-text="text"
            item-value="value"
            label="Keşfet Karoseli"
            hide-details
            outlined
            dense
            class="status-filter"
            @change="onCarouselFilterChange"
          ></v-select>
        </div>
        <div class="campaign-toolbar__actions">
          <admin-saved-filters
            storage-key="campaigns"
            :value="{ search: searchValue, status: selectedStatusFilter, carousel: selectedCarouselFilter }"
            @apply="applySavedCampaignFilters"
          />
          <v-menu
            offset-y
            :close-on-content-click="false"
          >
            <template #activator="{ on, attrs }">
              <v-btn
                icon
                title="Görünür sütunlar"
                aria-label="Görünür sütunlar"
                v-bind="attrs"
                v-on="on"
              >
                <v-icon>{{ icons.mdiTableColumn }}</v-icon>
              </v-btn>
            </template>
            <v-card class="pa-4">
              <v-checkbox
                v-for="header in headers"
                :key="header.value"
                v-model="visibleColumns"
                :value="header.value"
                :label="header.text"
                :disabled="['name', 'action'].includes(header.value)"
                dense
                hide-details
              />
            </v-card>
          </v-menu>
          <v-btn
            v-if="canManageUrlHealth"
            outlined
            small
            color="primary"
            :to="{ name: 'campaignUrlHealth' }"
          >
            <v-icon left>
              {{ icons.mdiLinkVariant }}
            </v-icon>
            URL Sağlığı
          </v-btn>
          <v-btn
            outlined
            small
            color="primary"
            @click="openImagePool"
          >
            <v-icon left>
              {{ icons.mdiImage }}
            </v-icon>
            Görsel Havuzu
          </v-btn>
          <v-btn
            color="primary"
            :disabled="saveLoading || editorImageUploading"
            @click="openCreateDialog"
          >
            {{ carouselMode ? 'Karosel Kampanyası Oluştur' : 'Kampanya Oluştur' }}
          </v-btn>
        </div>
      </div>

      <v-dialog
        v-model="createDialog"
        max-width="1180px"
        content-class="campaign-composer-dialog"
        :persistent="saveLoading || editorImageUploading"
      >
        <v-card class="campaign-create-card">
          <header class="campaign-create-header">
            <div>
              <h2>{{ candidate ? 'Kampanya / koleksiyon incelemesi' : 'Yeni kampanya oluştur' }}</h2>
              <p>{{ candidate ? candidate.brandName + ' · ' + candidateStatusLabel : 'Yeni taslak' }}</p>
            </div>
            <button
              type="button"
              class="composer-button"
              :disabled="saveLoading || editorImageUploading"
              @click="closeCreate"
            >
              Kapat
            </button>
          </header>
          <div class="campaign-create-body">
            <v-alert
              v-if="createDialogBootstrapping"
              dense
              outlined
              type="info"
              class="mb-4"
            >
              Offer ve kategori listeleri yükleniyor...
            </v-alert>
            <v-alert
              v-if="createError"
              dense
              outlined
              type="error"
              class="mb-4"
            >
              {{ createError }}
            </v-alert>
            <div
              v-if="!createDialogBootstrapping"
              class="campaign-create-grid"
            >
              <fieldset
                :disabled="composerReadonly || saveLoading"
                class="campaign-create-fields"
              >
                <label class="composer-label">Tür</label>
                <div class="composer-mode-picker">
                  <label
                    v-for="option in campaignKindOptions"
                    :key="option.value"
                  >
                    <input
                      v-model="newCampaign.campaignKind"
                      type="radio"
                      name="campaign-kind"
                      :value="option.value"
                    >
                    <span>{{ option.text }}</span>
                  </label>
                </div>
                <label
                  for="composer-offer"
                  class="composer-label"
                >Marka / Network Offer</label>
                <v-autocomplete
                  id="composer-offer"
                  v-model="newCampaign.mainCampaignId"
                  class="composer-offer"
                  outlined
                  dense
                  hide-details
                  :loading="offerLoading"
                  :disabled="!!candidate || saveLoading"
                  :items="mainCampaigns"
                  item-text="name"
                  item-value="id"
                  placeholder="Network Offer seçin"
                />
                <label
                  for="composer-name"
                  class="composer-label"
                >Kampanya / koleksiyon adı</label>
                <input
                  id="composer-name"
                  v-model="newCampaign.name"
                  class="composer-input"
                  type="text"
                  maxlength="255"
                  aria-describedby="composer-name-count"
                >
                <div
                  id="composer-name-count"
                  class="composer-character-count"
                >
                  {{ (newCampaign.name || '').length }} / 255 karakter
                </div>
                <label
                  for="composer-link"
                  class="composer-label"
                >Hedef bağlantı</label>
                <input
                  id="composer-link"
                  v-model="newCampaign.trackingLink"
                  class="composer-input"
                  type="url"
                  placeholder="https://..."
                >
                <label
                  for="composer-start"
                  class="composer-label"
                >Aktif olma tarihi (Türkiye saati)</label>
                <input
                  id="composer-start"
                  v-model="newCampaign.startDateTime"
                  class="composer-input"
                  type="datetime-local"
                  step="1"
                >
                <label
                  for="composer-end"
                  class="composer-label"
                >Bitiş tarihi (Türkiye saati)</label>
                <input
                  id="composer-end"
                  v-model="newCampaign.endDateTime"
                  class="composer-input"
                  type="datetime-local"
                  step="1"
                >
                <label
                  id="composer-category-label"
                  class="composer-label"
                >Kategoriler</label>
                <details class="composer-categories">
                  <summary aria-labelledby="composer-category-label">
                    {{ selectedCategoryNames || 'Kategori seçin' }}
                  </summary>
                  <div class="composer-category-options">
                    <label
                      v-for="category in categories"
                      :key="category.id"
                      class="composer-checkbox"
                    >
                      <input
                        v-model="newCampaign.categoryIds"
                        type="checkbox"
                        :value="category.id"
                      >
                      <span>{{ category.name }}</span>
                    </label>
                  </div>
                </details>
                <div class="composer-toggles">
                  <label class="composer-checkbox"><input
                    v-model="newCampaign.premiumOnly"
                    type="checkbox"
                  ><span>Premium</span></label>
                  <label class="composer-checkbox"><input
                    v-model="newCampaign.recommended"
                    type="checkbox"
                  ><span>Önerilen</span></label>
                  <label class="composer-checkbox"><input
                    v-model="newCampaign.sendPushNotification"
                    type="checkbox"
                  ><span>Push bildirimi</span></label>
                  <label class="composer-checkbox"><input
                    v-model="newCampaign.sendEmail"
                    type="checkbox"
                  ><span>E-posta</span></label>
                  <label class="composer-checkbox"><input
                    v-model="newCampaign.carouselCampaign"
                    type="checkbox"
                    :disabled="carouselMode"
                  ><span>Keşfet karoseli</span></label>
                </div>
                <div
                  v-if="newCampaign.carouselCampaign || carouselMode"
                  class="composer-carousel"
                >
                  <label class="composer-label">Karosel görseli</label>
                  <img
                    v-if="newCampaign.carouselImageUrl"
                    :src="newCampaign.carouselImageUrl"
                    :alt="newCampaign.carouselImageName || 'Seçili karosel görseli'"
                  >
                  <div class="composer-inline-actions">
                    <button
                      type="button"
                      class="composer-button"
                      @click="openCarouselImagePool('create')"
                    >
                      <v-icon small>
                        {{ icons.mdiImage }}
                      </v-icon> Görsel seç
                    </button>
                    <button
                      v-if="newCampaign.carouselImageId"
                      type="button"
                      class="composer-button"
                      @click="clearCarouselImageSelection('create')"
                    >
                      Seçimi temizle
                    </button>
                  </div>
                </div>
                <p class="composer-activation">
                  {{ newCampaign.startDateTime ? 'Aktif olma: ' + newCampaign.startDateTime.replace('T', ' ') : 'Onay sonrası hemen aktif' }}
                </p>
                <div
                  v-if="candidate"
                  class="candidate-evidence"
                >
                  <a
                    :href="candidate.sourceUrl"
                    target="_blank"
                    rel="noopener noreferrer"
                  >{{ candidate.brandName }} kaynak sayfası <v-icon small>{{ icons.mdiOpenInNew }}</v-icon></a>
                  <table class="candidate-comparison">
                    <thead><tr><th>Alan</th><th>Doğrulanmış kaynak</th><th>Yayın formu</th></tr></thead>
                    <tbody>
                      <tr
                        v-for="row in candidateDateComparison"
                        :key="row.label"
                      >
                        <th>{{ row.label }}</th><td>{{ row.source || 'Doğrulanmadı' }}</td><td>{{ row.current || '-' }}</td>
                      </tr>
                    </tbody>
                  </table>
                  <details>
                    <summary>Kaydedilen aday ve düzenlenen metin</summary>
                    <dl><dt>Kaydedilen başlık</dt><dd>{{ candidate.form.name }}</dd><dt>Düzenlenen başlık</dt><dd>{{ newCampaign.name }}</dd><dt>Kaydedilen önizleme</dt><dd>{{ candidate.form.shortDescription }}</dd><dt>Düzenlenen önizleme</dt><dd>{{ newCampaign.shortDescription }}</dd></dl>
                  </details>
                  <details>
                    <summary>Kaynak kanıtı ve inceleme notları</summary><pre>{{ candidateEvidence }}</pre>
                  </details>
                  <div
                    v-if="candidate.errors.length"
                    class="composer-warning"
                    role="status"
                  >
                    <div
                      v-for="message in candidate.errors"
                      :key="message"
                    >
                      {{ message }}
                    </div>
                  </div>
                  <label class="composer-checkbox composer-source-review">
                    <input
                      v-model="candidateReviewed"
                      type="checkbox"
                    >
                    <span>Kaynağı, görseldeki tarihleri ve kampanya koşullarını kontrol ettim</span>
                  </label>
                </div>
              </fieldset>
              <fieldset
                class="campaign-create-description"
                :disabled="composerReadonly || saveLoading"
              >
                <label
                  for="composer-preview"
                  class="composer-label"
                >Kampanya önizleme metni</label>
                <textarea
                  id="composer-preview"
                  v-model="newCampaign.shortDescription"
                  class="composer-input"
                  rows="2"
                  maxlength="255"
                ></textarea>
                <div class="composer-editor-heading">
                  <label class="composer-label">Açıklama</label>
                  <div class="composer-inline-actions">
                    <button
                      type="button"
                      class="composer-button"
                      :disabled="editorImageUploading"
                      @click="handleDescriptionImageUpload('createDescriptionEditor')"
                    >
                      <v-icon small>
                        {{ icons.mdiUpload }}
                      </v-icon> Görsel yükle
                    </button>
                    <button
                      type="button"
                      class="composer-button"
                      :disabled="editorImageUploading"
                      @click="openEditorImagePool('createDescriptionEditor')"
                    >
                      <v-icon small>
                        {{ icons.mdiImage }}
                      </v-icon> Havuzdan seç
                    </button>
                  </div>
                </div>
                <p
                  v-if="editorImageUploading"
                  class="composer-upload-status"
                  role="status"
                >
                  Görsel yükleniyor...
                </p>
                <quill-editor
                  ref="createDescriptionEditor"
                  v-model="newCampaign.description"
                  :disabled="composerReadonly || saveLoading"
                  :options="createDescriptionEditorOptions"
                  class="campaign-description-editor"
                  @change="normalizeEditorLinks('createDescriptionEditor')"
                />
                <div class="campaign-editor-toolbar mt-3">
                  <v-btn
                    small
                    outlined
                    :disabled="composerReadonly || saveLoading || !hasCardImage(newCampaign.description)"
                    @click="openCardCropper('createDescriptionEditor')"
                  >
                    <v-icon
                      left
                      small
                    >
                      {{ icons.mdiCrop }}
                    </v-icon>Kart kapağı · 4:3
                  </v-btn>
                  <v-btn
                    v-if="hasCardCrop(newCampaign.description)"
                    small
                    text
                    :disabled="composerReadonly || saveLoading"
                    @click="clearCardCrop('createDescriptionEditor')"
                  >
                    Kart kırpmasını kaldır
                  </v-btn>
                </div>
              </fieldset>
            </div>
          </div>
          <footer class="campaign-create-footer">
            <span class="composer-footnote">{{ candidate ? candidateStatusLabel : 'Yeni kampanya / koleksiyon' }}</span>
            <template v-if="candidate && candidateNavigationIds.length > 1">
              <v-btn
                text
                small
                :disabled="saveLoading || createDialogBootstrapping || candidateNavigationIndex <= 0"
                @click="navigateCandidate(-1)"
              >
                {{ candidate.status === 'PENDING' ? 'Kaydet ve önceki' : 'Önceki' }}
              </v-btn>
              <span class="text-caption">{{ candidateNavigationIndex + 1 }} / {{ candidateNavigationIds.length }}</span>
              <v-btn
                text
                small
                :disabled="saveLoading || createDialogBootstrapping || candidateNavigationIndex >= candidateNavigationIds.length - 1"
                @click="navigateCandidate(1)"
              >
                {{ candidate.status === 'PENDING' ? 'Kaydet ve sonraki' : 'Sonraki' }}
              </v-btn>
            </template>
            <button
              v-if="candidate && candidate.status === 'PENDING'"
              type="button"
              class="composer-button composer-button--danger"
              :disabled="saveLoading || editorImageUploading"
              @click="rejectCandidate"
            >
              Reddet
            </button>
            <button
              v-if="candidate && candidate.status === 'PENDING'"
              type="button"
              class="composer-button"
              :disabled="saveLoading || editorImageUploading"
              @click="saveCandidateDraft"
            >
              Taslağı kaydet
            </button>
            <button
              v-if="!candidate || candidate.status === 'PENDING'"
              type="button"
              class="composer-button composer-button--primary"
              :disabled="createDialogBootstrapping || saveLoading || editorImageUploading || (candidate && !candidateReviewed)"
              @click="saveCreate"
            >
              {{ saveLoading ? 'Kaydediliyor...' : candidate ? 'Onayla ve aktif et' : 'Oluştur ve aktif et' }}
            </button>
          </footer>
        </v-card>
      </v-dialog>

      <v-dialog
        v-model="imageDialog"
        max-width="1200px"
        :persistent="imageUploading"
      >
        <v-card
          class="image-dialog-card"
          outlined
        >
          <v-toolbar
            dense
            dark
            color="primary"
            :elevation="0"
          >
            <v-toolbar-title>Görsel Havuzu</v-toolbar-title>
            <v-text-field
              v-model="imageSearchValue"
              placeholder="Görsel ara"
              clearable
              dense
              dark
              hide-details
              class="ml-4"
              style="max-width: 300px;"
              @input="onImageSearch"
            ></v-text-field>
            <v-spacer></v-spacer>
            <v-btn
              icon
              :disabled="imageUploading"
              @click="closeImagePool"
            >
              <v-icon>{{ icons.mdiClose }}</v-icon>
            </v-btn>
          </v-toolbar>
          <v-card-text>
            <v-alert
              v-if="imageError"
              type="error"
              dense
              outlined
              class="mt-3"
            >
              {{ imageError }}
            </v-alert>
            <v-tabs
              v-model="imageSource"
              @change="onImageSourceChange"
            >
              <v-tab
                href="#uploads"
                :disabled="imageUploading"
              >
                Yüklenen görseller
              </v-tab>
              <v-tab
                href="#candidates"
                :disabled="imageUploading"
              >
                İnceleme görselleri
              </v-tab>
            </v-tabs>
            <p
              v-if="imageSource === 'candidates'"
              class="text-caption mt-3"
            >
              Kampanya ve koleksiyon incelemelerindeki görseller. Marka veya kampanya adıyla arayın;
              seçtiğiniz görsel tekrar yüklenmeden havuza eklenir. Sayfalama inceleme kaydı bazındadır.
            </p>
            <v-row>
              <v-col cols="12">
                <v-card
                  outlined
                  class="pa-3 new-image-card"
                >
                  <div class="text-subtitle-2 font-weight-medium mb-3">
                    Yeni Görsel Ekle
                  </div>
                  <v-row
                    align="center"
                    class="new-image-row"
                  >
                    <v-col
                      cols="12"
                      md="4"
                    >
                      <v-text-field
                        v-model="imageForm.fileName"
                        class="image-input-control"
                        outlined
                        dense
                        label="Görsel Adı"
                        placeholder="ör. banner-1.png"
                        maxlength="255"
                        :disabled="imageUploading"
                        hide-details
                      ></v-text-field>
                    </v-col>
                    <v-col
                      cols="12"
                      md="4"
                    >
                      <div class="d-flex align-center">
                        <v-btn
                          class="image-upload-btn"
                          outlined
                          color="primary"
                          :disabled="imageUploading"
                          @click="triggerImageSelect"
                        >
                          <v-icon left>
                            {{ icons.mdiUpload }}
                          </v-icon>
                          Görsel Seç
                        </v-btn>
                        <v-btn
                          class="image-submit-btn ml-2"
                          :loading="imageUploading"
                          :disabled="!imageFile || imageUploading"
                          color="primary"
                          @click="uploadImage"
                        >
                          Yükle
                        </v-btn>
                        <span
                          v-if="imageFileName"
                          class="ml-3 text-caption image-file-name"
                        >{{ imageFileName }}</span>
                        <input
                          ref="imageFileInput"
                          type="file"
                          accept="image/jpeg,image/png,image/webp"
                          style="display: none"
                          @change="onImageFileChange"
                        />
                      </div>
                    </v-col>
                    <v-col
                      cols="12"
                      md="4"
                      class="d-flex align-center"
                    ></v-col>
                  </v-row>
                </v-card>
              </v-col>
            </v-row>
            <v-divider class="my-4"></v-divider>
            <v-row>
              <v-col
                cols="12"
                md="6"
                class="d-flex align-center"
              >
                <span class="mr-2 text-caption">{{ imageSource === 'candidates' ? 'Sayfa başı inceleme:' : 'Sayfa başı:' }}</span>
                <v-select
                  v-model="imageItemsPerPage"
                  dense
                  outlined
                  hide-details
                  style="max-width: 100px"
                  :items="imagePerPageOptions"
                  @change="onImageItemsPerPageChange"
                ></v-select>
              </v-col>
              <v-col
                cols="12"
                md="6"
                class="d-flex justify-end"
              >
                <v-pagination
                  v-model="imagePage"
                  :length="imageTotalPages"
                  total-visible="7"
                  circle
                  @input="onImagePageChange"
                ></v-pagination>
              </v-col>
            </v-row>
            <v-progress-linear
              v-if="imageLoading"
              indeterminate
              class="my-3"
            ></v-progress-linear>
            <p
              v-else-if="!images.length"
              class="text-body-2 my-4"
            >
              {{ imageError ? 'Görseller yüklenemedi.' : 'Bu sayfada eşleşen görsel bulunamadı.' }}
            </p>
            <v-row>
              <v-col
                v-for="item in images"
                :key="item.id"
                cols="12"
                sm="6"
                md="4"
                lg="3"
                xl="2"
                class="image-col"
              >
                <v-card
                  outlined
                  class="image-card"
                >
                  <v-img
                    :src="item._src"
                    :lazy-src="item._src"
                    height="180"
                    contain
                    class="image-card__img"
                    @error="onImageError(item)"
                    @click="openImagePreview(item)"
                  >
                    <template v-slot:placeholder>
                      <v-row
                        class="fill-height ma-0"
                        align="center"
                        justify="center"
                      >
                        <v-progress-circular
                          indeterminate
                          color="primary"
                        ></v-progress-circular>
                      </v-row>
                    </template>
                  </v-img>
                  <v-card-text class="py-2">
                    <div class="d-flex align-center justify-space-between mb-2">
                      <span class="image-card__name">
                        {{ item._editing ? '' : (item._nameDraft || item.name) }}
                      </span>
                      <v-chip
                        small
                        :color="item._activeDraft ? 'success' : 'error'"
                        class="white--text"
                        outlined
                      >
                        {{ item.candidateId ? 'İnceleme' : (item._activeDraft ? 'Aktif' : 'Pasif') }}
                      </v-chip>
                    </div>
                  </v-card-text>
                  <v-card-actions class="py-2 px-3">
                    <v-btn
                      v-if="imageDialogMode === 'carousel'"
                      small
                      text
                      color="primary"
                      :disabled="imageUploading"
                      @click="selectCarouselImageFromPool(item)"
                    >
                      Karosele Seç
                    </v-btn>
                    <v-btn
                      v-else-if="imageDialogMode === 'editor'"
                      small
                      text
                      color="primary"
                      @click="insertImageFromPool(item)"
                    >
                      Editöre Ekle
                    </v-btn>
                    <v-spacer></v-spacer>
                    <v-btn
                      v-if="!item.candidateId"
                      icon
                      small
                      :loading="item._renameLoading"
                      @click="deleteImage(item)"
                    >
                      <v-icon color="error">
                        {{ icons.mdiDelete }}
                      </v-icon>
                    </v-btn>
                  </v-card-actions>
                </v-card>
              </v-col>
            </v-row>
            <v-row class="mt-2">
              <v-col
                cols="12"
                md="6"
                class="d-flex align-center"
              >
                <span class="mr-2 text-caption">{{ imageSource === 'candidates' ? 'Sayfa başı inceleme:' : 'Sayfa başı:' }}</span>
                <v-select
                  v-model="imageItemsPerPage"
                  dense
                  outlined
                  hide-details
                  style="max-width: 100px"
                  :items="imagePerPageOptions"
                  @change="onImageItemsPerPageChange"
                ></v-select>
              </v-col>
              <v-col
                cols="12"
                md="6"
                class="d-flex justify-end"
              >
                <v-pagination
                  v-model="imagePage"
                  :length="imageTotalPages"
                  total-visible="7"
                  circle
                  @input="onImagePageChange"
                ></v-pagination>
              </v-col>
            </v-row>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              text
              @click="closeImagePool"
            >
              Kapat
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
      <v-dialog
        v-model="imagePreviewDialog"
        max-width="900px"
      >
        <v-card outlined>
          <v-toolbar
            dense
            dark
            color="primary"
            :elevation="0"
          >
            <v-toolbar-title>{{ imagePreviewName || 'Görsel Önizleme' }}</v-toolbar-title>
            <v-spacer></v-spacer>
            <v-btn
              icon
              @click="closeImagePreview"
            >
              <v-icon>{{ icons.mdiClose }}</v-icon>
            </v-btn>
          </v-toolbar>
          <v-card-text>
            <v-img
              :src="imagePreviewSrc"
              contain
              max-width="100%"
              max-height="70vh"
            >
              <template v-slot:placeholder>
                <v-row
                  class="fill-height ma-0"
                  align="center"
                  justify="center"
                >
                  <v-progress-circular
                    indeterminate
                    color="primary"
                  ></v-progress-circular>
                </v-row>
              </template>
            </v-img>
          </v-card-text>
          <v-card-actions>
            <v-btn
              v-if="imageDialogMode === 'carousel' && imagePreviewItem"
              text
              color="primary"
              @click="selectCarouselImageFromPool(imagePreviewItem)"
            >
              <v-icon left>
                {{ icons.mdiImage }}
              </v-icon>
              Karosele Seç
            </v-btn>
            <v-btn
              text
              color="primary"
              :disabled="!imagePreviewSrc"
              @click="openCarouselCropper"
            >
              <v-icon left>
                {{ icons.mdiCrop }}
              </v-icon>
              Karosel İçin Kırp
            </v-btn>
            <v-spacer></v-spacer>
            <v-btn
              text
              color="primary"
              @click="closeImagePreview"
            >
              Kapat
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
      <v-dialog
        v-model="imageCropDialog"
        max-width="960px"
        scrollable
        persistent
      >
        <v-card outlined>
          <v-toolbar
            dense
            dark
            color="primary"
            :elevation="0"
          >
            <v-toolbar-title>{{ cardCropEditor ? 'Kart kapağı · 4:3' : 'Karosel Safe Zone' }}</v-toolbar-title>
            <v-spacer></v-spacer>
            <v-btn
              icon
              @click="closeCarouselCropper"
            >
              <v-icon>{{ icons.mdiClose }}</v-icon>
            </v-btn>
          </v-toolbar>
          <v-card-text class="pt-4">
            <v-alert
              v-if="imageCropError"
              dense
              outlined
              type="error"
              class="mb-4"
            >
              {{ imageCropError }}
            </v-alert>
            <div
              v-if="!cardCropEditor"
              class="text-subtitle-2 font-weight-medium mb-2"
            >
              Safe zone dikdörtgenini sürükleyerek kırpılacak alanı seç.
            </div>
            <div
              ref="imageCropShell"
              class="carousel-crop-shell"
            >
              <img
                v-if="imageCropSrc"
                :key="`${cardCropEditor ? 'card' : 'carousel'}:${imageCropSrc}`"
                ref="imageCropImage"
                :src="imageCropSrc"
                :crossorigin="cardCropEditor ? null : 'anonymous'"
                class="carousel-crop-image"
                :alt="cardCropEditor ? 'Kart kapağı kırpma görseli' : 'Karosel kırpma görseli'"
                @load="onCropImageLoad"
                @error="onCropImageError"
              />
              <div
                v-if="imageCropLoading && !imageCropLoaded"
                class="carousel-crop-loading"
                role="status"
                aria-label="Görsel yükleniyor"
              >
                <v-progress-circular
                  indeterminate
                  color="primary"
                ></v-progress-circular>
              </div>
              <div
                v-if="cropReady"
                class="carousel-crop-selection"
                :style="cropSelectionStyle"
                tabindex="0"
                role="group"
                aria-label="Kırpma alanı"
                @keydown="moveCropWithKeyboard"
                @mousedown.prevent="startCropDrag"
                @touchstart.prevent="startCropDrag"
              >
                <div class="carousel-crop-selection__label">
                  {{ cardCropEditor ? '4:3' : 'Safe zone' }}
                </div>
              </div>
            </div>
            <div class="carousel-crop-controls">
              <div>
                <div class="text-subtitle-2 font-weight-medium">
                  {{ cardCropEditor ? 'Kırpma boyutu · 4:3' : 'Safe zone boyutu · 35:18' }}
                </div>
                <v-slider
                  v-model="imageCropScale"
                  :min="cardCropEditor ? 10 : 45"
                  :max="100"
                  :step="1"
                  :disabled="!cropReady || imageCropLoading"
                  thumb-label
                  hide-details
                  :aria-label="cardCropEditor ? 'Kırpma boyutu' : 'Safe zone boyutu'"
                  @input="onCropScaleInput"
                ></v-slider>
                <div
                  v-if="!cardCropEditor"
                  class="text-caption grey--text text--darken-1"
                >
                  Bu işlem mevcut görseli bozmaz. Kırpılmış yeni bir görsel havuza yüklenir.
                </div>
              </div>
              <div>
                <div class="text-caption mb-1">
                  Önizleme
                </div>
                <div class="carousel-crop-preview">
                  <div
                    class="carousel-crop-preview__viewport"
                    :style="{ aspectRatio: String(carouselCropAspectRatio) }"
                  >
                    <img
                      v-if="cropReady"
                      :src="imageCropSrc"
                      :alt="cardCropEditor ? 'Kart kapağı kırpma önizleme' : 'Karosel kırpma önizleme'"
                      class="carousel-crop-preview__img"
                      :style="cropPreviewImageStyle"
                    />
                  </div>
                </div>
              </div>
            </div>
          </v-card-text>
          <v-card-actions>
            <v-btn
              text
              @click="closeCarouselCropper"
            >
              İptal
            </v-btn>
            <v-spacer></v-spacer>
            <v-btn
              color="primary"
              :loading="imageCropLoading"
              :disabled="!cropReady || imageCropLoading"
              @click="cardCropEditor ? applyCardCrop() : uploadCroppedCarouselImage()"
            >
              {{ cardCropEditor ? 'Kırpmayı uygula' : 'Kırpılmış Görseli Yükle' }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
      <v-alert
        v-if="!expiryReviewMode && !candidateMode && tableError"
        dense
        outlined
        type="error"
        class="mx-4 mb-0"
      >
        {{ tableError }}
      </v-alert>

      <div
        v-if="expiryReviewMode"
        class="expiry-review-toolbar"
      >
        <div class="expiry-review-toolbar__copy">
          <strong>Bitiş tarihi yaklaşan kampanyalar</strong>
          <span>
            Bitişine 4 gün kalan veya tarihi geçen aktif kampanyaları marka sitesinden manuel kontrol edin.
          </span>
        </div>
        <div class="expiry-review-toolbar__actions">
          <v-text-field
            v-model="expiryReviewSearch"
            append-icon="mdi-magnify"
            label="İnceleme listesinde ara"
            single-line
            hide-details
            outlined
            dense
            clearable
            class="expiry-review-search"
            @input="onExpiryReviewSearch"
          ></v-text-field>
          <v-btn
            outlined
            small
            color="primary"
            :loading="expiryReviewLoading"
            @click="loadExpiryReviewData"
          >
            Yenile
          </v-btn>
        </div>
      </div>

      <v-alert
        v-if="expiryReviewMode && expiryReviewError"
        dense
        outlined
        type="error"
        class="mx-4 mb-0"
      >
        {{ expiryReviewError }}
      </v-alert>

      <v-alert
        v-if="expiryReviewMode && expiryReviewSuccess"
        dense
        outlined
        type="success"
        class="mx-4 mb-0"
        dismissible
      >
        {{ expiryReviewSuccess }}
      </v-alert>

      <v-data-table
        v-if="expiryReviewMode"
        dense
        :loading="expiryReviewLoading"
        loading-text="Bitiş kontrolü listesi yükleniyor"
        :headers="expiryReviewHeaders"
        :items="expiryReviewCampaigns"
        :options.sync="expiryReviewOptions"
        :server-items-length="expiryReviewTotal"
        :footer-props="{
          itemsPerPageOptions: [10, 20, 50, 100],
        }"
        class="elevation-1 expiry-review-table"
        @update:options="onUpdateExpiryReviewOptions"
      >
        <template v-slot:item.logoUrl="{ item }">
          <v-avatar
            size="40"
            tile
          >
            <v-img
              :src="item.logoUrl || notImage"
              contain
            ></v-img>
          </v-avatar>
        </template>

        <template v-slot:item.endDateTime="{ item }">
          {{ formatDateTime(item.endDateTime) }}
        </template>

        <template v-slot:item.reviewWindow="{ item }">
          <v-chip
            small
            outlined
            :color="getExpiryReviewColor(item.endDateTime)"
          >
            {{ getExpiryReviewLabel(item.endDateTime) }}
          </v-chip>
        </template>

        <template v-slot:item.realLink="{ item }">
          <v-btn
            v-if="item.realLink"
            x-small
            text
            color="primary"
            @click="openCampaignWebsite(item)"
          >
            <v-icon
              x-small
              left
            >
              {{ icons.mdiOpenInNew }}
            </v-icon>
            Siteyi Aç
          </v-btn>
          <span v-else>-</span>
        </template>

        <template v-slot:item.reviewActions="{ item }">
          <div class="expiry-review-row-actions">
            <v-btn
              x-small
              outlined
              color="success"
              :loading="isExpiryReviewActionLoading(item.id, 'extend')"
              :disabled="isExpiryReviewItemBusy(item.id)"
              @click="extendExpiryReview(item)"
            >
              +1 Gün
            </v-btn>
            <v-btn
              x-small
              outlined
              color="primary"
              :disabled="isExpiryReviewItemBusy(item.id)"
              @click="openExpiryReviewDateDialog(item)"
            >
              Yeni Tarih
            </v-btn>
            <v-btn
              x-small
              color="error"
              :disabled="isExpiryReviewItemBusy(item.id)"
              @click="openExpiryReviewCompleteDialog(item)"
            >
              Bitti
            </v-btn>
          </div>
        </template>
      </v-data-table>

      <v-data-table
        v-else-if="!candidateMode"
        dense
        :loading="loading"
        loading-text="Yükleniyor"
        :headers="visibleHeaders"
        :items="campaigns"
        :options.sync="options"
        :server-items-length="totalElements"
        :footer-props="{
          itemsPerPageOptions: [10, 20, 50, 100],
        }"
        class="elevation-1 campaign-table"
        @update:options="onUpdateTableOptions"
      >
        <template v-slot:item.name="{ item }">
          <strong>{{ item.name }}</strong><div class="text-caption">
            {{ item.mainCampaignName }} · #{{ item.id }} · {{ formatCampaignKind(item.campaignKind) }}
          </div>
        </template>
        <template v-slot:item.period="{ item }">
          <div>{{ formatDateTime(item.startDateTime) }}</div><div class="text-caption">
            Bitiş: {{ formatDateTime(item.endDateTime) }}
          </div>
        </template>
        <template v-slot:item.placements="{ item }">
          <div>{{ item.premiumOnly ? 'Premium' : 'Herkese açık' }}</div><div class="text-caption">
            {{ [item.recommended && 'Önerilen', item.carouselCampaign && 'Karosel'].filter(Boolean).join(' · ') || 'Standart liste' }}
          </div>
        </template>
        <template v-slot:item.campaignKind="{ item }">
          <v-chip
            small
            outlined
            color="primary"
          >
            {{ formatCampaignKind(item.campaignKind) }}
          </v-chip>
        </template>

        <template v-slot:item.logoUrl="{ item }">
          <v-avatar
            size="40"
            tile
          >
            <v-img
              :src="item.logoUrl || notImage"
              contain
            ></v-img>
          </v-avatar>
        </template>

        <template v-slot:item.status="{ item }">
          <v-chip
            small
            outlined
            :color="getCampaignStatusColor(item.status)"
          >
            {{ getCampaignStatusLabel(item.status) }}
          </v-chip>
        </template>

        <template v-slot:item.premiumOnly="{ item }">
          <v-chip
            small
            :color="item.premiumOnly ? 'warning' : 'secondary'"
            outlined
          >
            {{ item.premiumOnly ? 'Premium' : 'Free' }}
          </v-chip>
        </template>

        <template v-slot:item.recommended="{ item }">
          <v-chip
            small
            :color="item.recommended ? 'info' : 'secondary'"
            outlined
          >
            {{ item.recommended ? 'Evet' : 'Hayır' }}
          </v-chip>
        </template>

        <template v-slot:item.carouselCampaign="{ item }">
          <v-chip
            small
            :color="item.carouselCampaign ? 'success' : 'secondary'"
            outlined
          >
            {{ item.carouselCampaign ? 'Evet' : 'Hayır' }}
          </v-chip>
        </template>

        <template v-slot:item.mainCampaignPayout="{ item }">
          {{ formatNetworkPayout(item) }}
        </template>

        <template v-slot:item.created="{ item }">
          {{ formatDate(item.created) }}
        </template>

        <template v-slot:item.startDateTime="{ item }">
          {{ formatNullableDateTime(item.startDateTime) }}
        </template>

        <template v-slot:item.endDateTime="{ item }">
          {{ formatDateTime(item.endDateTime) }}
        </template>

        <template v-slot:item.modified="{ item }">
          {{ formatDate(item.modified) }}
        </template>

        <template v-slot:item.categories="{ item }">
          <div
            class="campaign-categories-cell"
            :title="formatCategories(item.categories, item.categoriesString)"
          >
            {{ formatCategories(item.categories, item.categoriesString) }}
          </div>
        </template>

        <template v-slot:item.action="{ item }">
          <div class="d-flex">
            <v-btn
              icon
              small
              color="primary"
              title="Kampanyayı düzenle"
              aria-label="Kampanyayı düzenle"
              @click="editItem(item)"
            >
              <v-icon small>
                {{ icons.mdiPencil }}
              </v-icon>
            </v-btn>
            <v-btn
              v-if="canManageUrlHealth"
              icon
              small
              title="URL sağlığını incele"
              aria-label="URL sağlığını incele"
              :to="{ name: 'campaignUrlHealth', query: { campaignId: String(item.id) } }"
            >
              <v-icon small>
                {{ icons.mdiLinkVariant }}
              </v-icon>
            </v-btn>
            <v-btn
              icon
              small
              color="error"
              title="Kampanyayı tamamla"
              aria-label="Kampanyayı tamamla"
              @click="openDeleteDialog(item)"
            >
              <v-icon small>
                {{ icons.mdiDelete }}
              </v-icon>
            </v-btn>
          </div>
        </template>
      </v-data-table>

      <v-dialog
        v-model="deleteDialog"
        max-width="500px"
        :persistent="deleteLoading"
      >
        <v-card>
          <v-card-title class="text-h5">
            Kampanyayı Tamamlanmışa Çek
          </v-card-title>
          <v-card-text>
            <strong>{{ deleteItem.name }}</strong> kampanyası tamamlanmış statüsüne çekilecek ve bağlı linkler tamamlanacak.
            <v-alert
              v-if="deleteError"
              type="error"
              dense
              outlined
              class="mt-3"
            >
              {{ deleteError }}
            </v-alert>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              color="primary"
              text
              :disabled="deleteLoading"
              @click="closeDeleteDialog"
            >
              İptal
            </v-btn>
            <v-btn
              color="error"
              :loading="deleteLoading"
              @click="confirmDelete"
            >
              Tamamlanmış Yap
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-dialog
        v-model="expiryReviewDateDialog"
        max-width="460px"
      >
        <v-card>
          <v-card-title class="text-h5">
            Yeni Bitiş Tarihi
          </v-card-title>
          <v-card-text>
            <div class="mb-4">
              <strong>{{ expiryReviewItem.name }}</strong>
            </div>
            <v-alert
              v-if="expiryReviewDialogError"
              dense
              outlined
              type="error"
              class="mb-4"
            >
              {{ expiryReviewDialogError }}
            </v-alert>
            <v-text-field
              v-model="expiryReviewNewEndDate"
              outlined
              dense
              type="date"
              label="Bitiş Tarihi"
              :min="todayIstanbulInput"
              :rules="requiredRules"
            ></v-text-field>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              text
              @click="closeExpiryReviewDateDialog"
            >
              İptal
            </v-btn>
            <v-btn
              color="primary"
              :loading="isExpiryReviewActionLoading(expiryReviewItem.id, 'date')"
              :disabled="!expiryReviewNewEndDate"
              @click="saveExpiryReviewDate"
            >
              Tarihi Güncelle
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-dialog
        v-model="expiryReviewCompleteDialog"
        max-width="520px"
      >
        <v-card>
          <v-card-title class="text-h5">
            Kampanya Bitti mi?
          </v-card-title>
          <v-card-text>
            <v-alert
              v-if="expiryReviewDialogError"
              dense
              outlined
              type="error"
              class="mb-4"
            >
              {{ expiryReviewDialogError }}
            </v-alert>
            <strong>{{ expiryReviewItem.name }}</strong> tamamlanmışa çekilecek ve bağlı aktif linkler kapatılacak.
            Bu işlem kullanıcıya ayrıca bildirim göndermez.
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              text
              @click="closeExpiryReviewCompleteDialog"
            >
              İptal
            </v-btn>
            <v-btn
              color="error"
              :loading="isExpiryReviewActionLoading(expiryReviewItem.id, 'complete')"
              @click="completeExpiryReview"
            >
              Bitti, Tamamla
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-dialog
        v-model="editDialog"
        max-width="1180px"
        content-class="campaign-composer-dialog"
        :persistent="saveLoading || editorImageUploading"
      >
        <v-card class="campaign-create-card">
          <header class="campaign-create-header">
            <div>
              <h2>Kampanya / koleksiyon düzenle</h2>
              <p>{{ editedCampaign.mainCampaignName }} · #{{ editedCampaign.id }}</p>
            </div>
            <button
              type="button"
              class="composer-button"
              :disabled="saveLoading || editorImageUploading"
              @click="closeEdit"
            >
              Kapat
            </button>
          </header>
          <div class="campaign-create-body">
            <v-alert
              v-if="editError"
              dense
              outlined
              type="error"
              class="mb-4"
              role="alert"
            >
              {{ editError }}
            </v-alert>
            <v-alert
              v-if="editSuccess"
              dense
              outlined
              type="success"
              class="mb-4"
              role="status"
            >
              {{ editSuccess }}
            </v-alert>
            <v-alert
              v-if="editDialogBootstrapping"
              dense
              outlined
              type="info"
              class="mb-4"
            >
              Offer ve kategori listeleri yükleniyor...
            </v-alert>
            <div
              v-if="!editDialogBootstrapping"
              class="campaign-create-grid"
            >
              <fieldset
                :disabled="saveLoading || editorImageUploading"
                class="campaign-create-fields"
              >
                <label class="composer-label">Tür</label>
                <div class="composer-mode-picker">
                  <label
                    v-for="option in campaignKindOptions"
                    :key="option.value"
                  >
                    <input
                      v-model="editedCampaign.campaignKind"
                      type="radio"
                      name="edit-campaign-kind"
                      :value="option.value"
                    >
                    <span>{{ option.text }}</span>
                  </label>
                </div>
                <label
                  for="edit-composer-offer"
                  class="composer-label"
                >Marka / Network Offer</label>
                <v-autocomplete
                  id="edit-composer-offer"
                  v-model="editedCampaign.mainCampaignId"
                  class="composer-offer"
                  outlined
                  dense
                  hide-details
                  :loading="offerLoading"
                  :disabled="saveLoading || editorImageUploading"
                  :items="editOfferItems"
                  item-text="name"
                  item-value="id"
                  placeholder="Network Offer seçin"
                />
                <label
                  for="edit-composer-status"
                  class="composer-label"
                >Durum</label>
                <select
                  id="edit-composer-status"
                  v-model="editedCampaign.status"
                  class="composer-input"
                >
                  <option
                    v-for="option in campaignStatusOptions"
                    :key="option.value"
                    :value="option.value"
                  >
                    {{ option.text }}
                  </option>
                </select>
                <label
                  for="edit-composer-name"
                  class="composer-label"
                >Kampanya / koleksiyon adı</label>
                <input
                  id="edit-composer-name"
                  v-model="editedCampaign.name"
                  class="composer-input"
                  type="text"
                  maxlength="255"
                  aria-describedby="edit-composer-name-count"
                >
                <div
                  id="edit-composer-name-count"
                  class="composer-character-count"
                >
                  {{ (editedCampaign.name || '').length }} / 255 karakter
                </div>
                <label
                  for="edit-composer-link"
                  class="composer-label"
                >Hedef bağlantı</label>
                <input
                  id="edit-composer-link"
                  v-model="editedCampaign.realLink"
                  class="composer-input"
                  type="url"
                  placeholder="https://..."
                >
                <label
                  for="edit-composer-redirect"
                  class="composer-label"
                >Yönlendirme bağlantısı</label>
                <input
                  id="edit-composer-redirect"
                  :value="editedCampaign.redirectLink"
                  class="composer-input"
                  type="url"
                  readonly
                >
                <label
                  for="edit-composer-start"
                  class="composer-label"
                >Aktif olma tarihi (Türkiye saati)</label>
                <input
                  id="edit-composer-start"
                  v-model="editedCampaign.startDateTime"
                  class="composer-input"
                  type="datetime-local"
                  step="1"
                >
                <label
                  for="edit-composer-end"
                  class="composer-label"
                >Bitiş tarihi (Türkiye saati)</label>
                <input
                  id="edit-composer-end"
                  v-model="editedCampaign.endDateTime"
                  class="composer-input"
                  type="datetime-local"
                  step="1"
                >
                <label
                  id="edit-composer-category-label"
                  class="composer-label"
                >Kategoriler</label>
                <details class="composer-categories">
                  <summary aria-labelledby="edit-composer-category-label">
                    {{ editedCategoryNames || 'Kategori seçin' }}
                  </summary>
                  <div class="composer-category-options">
                    <label
                      v-for="category in categories"
                      :key="category.id"
                      class="composer-checkbox"
                    >
                      <input
                        v-model="editedCampaign.categoryIds"
                        type="checkbox"
                        :value="category.id"
                      >
                      <span>{{ category.name }}</span>
                    </label>
                  </div>
                </details>
                <div class="composer-toggles">
                  <label class="composer-checkbox"><input
                    v-model="editedCampaign.premiumOnly"
                    type="checkbox"
                  ><span>Premium</span></label>
                  <label class="composer-checkbox"><input
                    v-model="editedCampaign.recommended"
                    type="checkbox"
                  ><span>Önerilen</span></label>
                  <label class="composer-checkbox"><input
                    v-model="editedCampaign.carouselCampaign"
                    type="checkbox"
                  ><span>Keşfet karoseli</span></label>
                  <label
                    v-if="editedCampaign.status === 'ACTIVE'"
                    class="composer-checkbox"
                    title="Kampanya karoseldeyse karosel sırası da yenilenir; karosel üyeliği değişmez."
                  ><input
                    v-model="editedCampaign.promoteAsNew"
                    type="checkbox"
                  ><span>Yeniler'e tekrar taşı</span></label>
                </div>
                <div
                  v-if="editedCampaign.carouselCampaign || carouselMode"
                  class="composer-carousel"
                >
                  <label class="composer-label">Karosel görseli</label>
                  <img
                    v-if="editedCampaign.carouselImageUrl"
                    :src="editedCampaign.carouselImageUrl"
                    :alt="editedCampaign.carouselImageName || 'Seçili karosel görseli'"
                  >
                  <div class="composer-inline-actions">
                    <button
                      type="button"
                      class="composer-button"
                      @click="openCarouselImagePool('edit')"
                    >
                      <v-icon small>
                        {{ icons.mdiImage }}
                      </v-icon> Görsel seç
                    </button>
                    <button
                      v-if="editedCampaign.carouselImageId"
                      type="button"
                      class="composer-button"
                      @click="clearCarouselImageSelection('edit')"
                    >
                      Seçimi temizle
                    </button>
                  </div>
                </div>
              </fieldset>
              <fieldset
                class="campaign-create-description"
                :disabled="saveLoading || editorImageUploading"
              >
                <label
                  for="edit-composer-preview"
                  class="composer-label"
                >Kampanya önizleme metni</label>
                <textarea
                  id="edit-composer-preview"
                  v-model="editedCampaign.shortDescription"
                  class="composer-input"
                  rows="2"
                  maxlength="255"
                ></textarea>
                <div class="composer-editor-heading">
                  <label class="composer-label">Açıklama</label>
                  <div class="composer-inline-actions">
                    <button
                      type="button"
                      class="composer-button"
                      @click="handleDescriptionImageUpload('editDescriptionEditor')"
                    >
                      <v-icon small>
                        {{ icons.mdiUpload }}
                      </v-icon> Görsel yükle
                    </button>
                    <button
                      type="button"
                      class="composer-button"
                      @click="openEditorImagePool('editDescriptionEditor')"
                    >
                      <v-icon small>
                        {{ icons.mdiImage }}
                      </v-icon> Havuzdan seç
                    </button>
                  </div>
                </div>
                <p
                  v-if="editorImageUploading"
                  class="composer-upload-status"
                  role="status"
                >
                  Görsel yükleniyor...
                </p>
                <quill-editor
                  ref="editDescriptionEditor"
                  v-model="editedCampaign.description"
                  :disabled="saveLoading"
                  :options="editDescriptionEditorOptions"
                  class="campaign-description-editor"
                  @change="normalizeEditorLinks('editDescriptionEditor')"
                />
                <div class="campaign-editor-toolbar mt-3">
                  <v-btn
                    small
                    outlined
                    :disabled="saveLoading || !hasCardImage(editedCampaign.description)"
                    @click="openCardCropper('editDescriptionEditor')"
                  >
                    <v-icon
                      left
                      small
                    >
                      {{ icons.mdiCrop }}
                    </v-icon>Kart kapağı · 4:3
                  </v-btn>
                  <v-btn
                    v-if="hasCardCrop(editedCampaign.description)"
                    small
                    text
                    :disabled="saveLoading"
                    @click="clearCardCrop('editDescriptionEditor')"
                  >
                    Kart kırpmasını kaldır
                  </v-btn>
                </div>
              </fieldset>
            </div>
          </div>
          <footer class="campaign-create-footer">
            <span class="composer-footnote">Kampanya #{{ editedCampaign.id }}</span>
            <button
              type="button"
              class="composer-button"
              :disabled="saveLoading || editorImageUploading"
              @click="closeEdit"
            >
              İptal
            </button>
            <button
              type="button"
              class="composer-button composer-button--primary"
              :disabled="editDialogBootstrapping || saveLoading || editorImageUploading"
              @click="saveEdit"
            >
              {{ saveLoading ? 'Kaydediliyor...' : 'Değişiklikleri kaydet' }}
            </button>
          </footer>
        </v-card>
      </v-dialog>

      <v-dialog
        v-model="statusDialog"
        max-width="500px"
      >
        <v-card>
          <v-toolbar
            dense
            dark
            color="primary"
            :elevation="0"
          >
            <v-toolbar-title>Kampanya Durumları</v-toolbar-title>
            <v-spacer></v-spacer>
            <v-btn
              icon
              @click="closeStatusDialog"
            >
              <v-icon>{{ icons.mdiClose }}</v-icon>
            </v-btn>
          </v-toolbar>
          <v-card-text class="mt-4">
            <v-alert
              v-if="statusError"
              type="warning"
              dense
              outlined
              class="mb-4"
            >
              {{ statusError }}
            </v-alert>
            <v-row>
              <v-col cols="12">
                <v-switch
                  v-model="toggleActive"
                  :label="toggleActive ? 'Kampanya Aktif' : 'Kampanya Tamamlanmış'"
                  :color="toggleActive ? 'success' : 'secondary'"
                  :loading="statusLoading"
                  @change="updateStatus"
                ></v-switch>
              </v-col>
              <v-col cols="12">
                <v-switch
                  v-model="toggleRecommended"
                  label="Önerilen Kampanya mı?"
                  color="info"
                  :loading="statusLoading"
                  @change="updateRecommended"
                ></v-switch>
              </v-col>
              <v-col cols="12">
                <v-switch
                  v-model="togglePremium"
                  label="Premium Kampanya mı?"
                  color="warning"
                  :loading="statusLoading"
                  @change="updatePremiumOnly"
                ></v-switch>
              </v-col>
            </v-row>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              text
              @click="closeStatusDialog"
            >
              Kapat
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-card>
  </div>
</template>

<script>
import axios from 'axios'
import { Quill } from 'vue-quill-editor'
import {
  mdiClose, mdiPencil, mdiUpload, mdiDelete, mdiImage, mdiCrop, mdiLinkVariant, mdiOpenInNew, mdiTableColumn,
} from '@mdi/js'
import { parseCardCrop, cropFromSelection } from '@/utils/campaignCardCrop'
import { getAccessToken, getPanelSession, hasAdminAccess } from '@/utils/panelSession'
import AdminPageHeader from '@/components/admin/AdminPageHeader.vue'
import AdminSavedFilters from '@/components/admin/AdminSavedFilters.vue'
import { isAuthError, resolveApiErrorMessage } from '@/utils/apiErrors'
import serializeParams from '@/utils/httpParams'
import notImage from '@/assets/images/notImage.png'
import CampaignCandidateQueue from './CampaignCandidateQueue.vue'

const campaignEditorToolbar = [
  [{ header: [1, 2, 3, 4, 5, 6, false] }],
  ['bold', 'italic', 'underline'],
  [{ color: [] }],
  [{ list: 'ordered' }, { list: 'bullet' }],
  ['link', 'clean'],
]

// Shared by route instances so navigating away cannot submit a second activation.
let campaignActivationInFlight = false

export default {
  name: 'CampaignsList',
  components: {
    AdminSavedFilters,
    AdminPageHeader,
    CampaignCandidateQueue,
  },
  props: {
    carouselMode: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      notImage,
      campaigns: [],
      campaignTab: 0,
      candidate: null,
      candidateNavigationIds: [],
      candidateReviewed: false,
      loading: false,
      tableError: '',
      campaignSaveSuccess: '',
      activationPending: false,
      activationPendingName: '',
      createError: '',
      editError: '',
      editSuccess: '',
      createDialogBootstrapping: false,
      editDialogBootstrapping: false,
      saveLoading: false,
      categoryLoading: false,
      offerLoading: false,
      categoryRequest: null,
      offerRequest: null,
      searchValue: '',
      visibleColumns: ['logoUrl', 'name', 'status', 'period', 'placements', 'mainCampaignSourceName', 'categories', 'action'],
      searchTimeout: null,
      headers: [
        { text: 'Logo', value: 'logoUrl', sortable: false },
        { text: 'Kampanya / Marka', value: 'name', sortable: true },
        { text: 'Durum', value: 'status', sortable: true },
        { text: 'Yayın tarihleri', value: 'period', sortable: false },
        { text: 'Yayın alanları', value: 'placements', sortable: false },
        { text: 'Network', value: 'mainCampaignSourceName', sortable: true },
        { text: 'Kategori', value: 'categories', sortable: false },
        { text: 'İşlemler', value: 'action', sortable: false },
      ],
      expiryReviewHeaders: [
        { text: 'ID', value: 'id', sortable: true },
        { text: 'Logo', value: 'logoUrl', sortable: false },
        { text: 'Marka / Offer', value: 'mainCampaignName', sortable: false },
        { text: 'Kampanya Adı', value: 'name', sortable: true },
        { text: 'Bitiş Tarihi', value: 'endDateTime', sortable: true },
        { text: 'Kontrol', value: 'reviewWindow', sortable: false },
        { text: 'Marka Sitesi', value: 'realLink', sortable: false },
        {
          text: 'İşlemler', value: 'reviewActions', sortable: false, width: 300,
        },
      ],
      expiryReviewCampaigns: [],
      expiryReviewLoading: false,
      expiryReviewError: '',
      expiryReviewSuccess: '',
      expiryReviewSearch: '',
      expiryReviewSearchTimeout: null,
      expiryReviewTotal: 0,
      expiryReviewOptions: {
        page: 1,
        itemsPerPage: 10,
        sortBy: ['endDateTime'],
        sortDesc: [false],
      },
      expiryReviewParams: {
        page: 0,
        size: 10,
        sortBy: 'endDate',
        sortDirection: 'ASC',
      },
      expiryReviewAction: '',
      expiryReviewItem: {},
      expiryReviewDateDialog: false,
      expiryReviewCompleteDialog: false,
      expiryReviewNewEndDate: '',
      expiryReviewDialogError: '',
      params: {
        searchValue: '',
        page: 0,
        size: 10,
        sortBy: 'created',
        sortDirection: 'DESC',
      },
      options: {},
      totalElements: 0,
      requestOptions: {
        headers: { Authorization: `Bearer ${getAccessToken()}` },
      },
      createDialog: false,
      editorImageUploading: false,
      createDescriptionEditorOptions: {
        modules: {
          toolbar: {
            container: campaignEditorToolbar,
            handlers: {
              image: () => this.handleDescriptionImageUpload('createDescriptionEditor'),
            },
          },
        },
        placeholder: 'Kampanya detay açıklamasını girin...',
        theme: 'snow',
      },
      editDescriptionEditorOptions: {
        modules: {
          toolbar: {
            container: campaignEditorToolbar,
            handlers: {
              image: () => this.handleDescriptionImageUpload('editDescriptionEditor'),
            },
          },
        },
        placeholder: 'Kampanya detay açıklamasını güncelleyin...',
        theme: 'snow',
      },
      imageDialog: false,
      imageDialogMode: 'manage',
      imageLoading: false,
      imageUploading: false,
      imageError: '',
      imageSource: 'uploads',
      imageRequestId: 0,
      imageEditorTargetRef: '',
      imageCarouselTarget: '',
      images: [],
      imageForm: {
        fileName: '',
      },
      imageFile: null,
      imageFileName: '',
      imagePage: 1,
      imageItemsPerPage: 10,
      imagePerPageOptions: [10, 25, 50],
      imageTotalElements: 0,
      imageTotalPagesOverride: null,
      imageSearchValue: '',
      imageSortBy: 'created',
      imageSortDirection: 'DESC',
      imagePreviewDialog: false,
      imagePreviewSrc: '',
      imagePreviewName: '',
      imagePreviewItem: null,
      imageCropDialog: false,
      cardCropEditor: '',
      cardCropSource: '',
      cardCropInitial: null,
      imageCropSrc: '',
      imageCropObjectUrl: '',
      imageCropName: '',
      imageCropItem: null,
      imageCropScale: 72,
      imageCropLoading: false,
      imageCropLoaded: false,
      imageCropError: '',
      imageCropPreviewSrc: '',
      cropMeta: {
        naturalWidth: 0,
        naturalHeight: 0,
        offsetX: 0,
        offsetY: 0,
        renderedWidth: 0,
        renderedHeight: 0,
      },
      cropRect: {
        x: 0,
        y: 0,
        width: 0,
        height: 0,
      },
      cropDragState: null,
      cropMeasureTimer: null,
      cropMeasureFrame: null,
      renameForm: {
        id: null,
        name: '',
      },
      newCampaign: {
        name: '',
        description: '',
        shortDescription: '',
        premiumOnly: false,
        sendPushNotification: false,
        sendEmail: false,
        recommended: false,
        carouselCampaign: false,
        campaignKind: 'CAMPAIGN',
        mainCampaignId: null,
        carouselImageId: null,
        carouselImageUrl: '',
        carouselImageName: '',
        trackingLink: '',
        realLink: '',
        redirectLink: '',
        startDateTime: '',
        endDateTime: '',
        categoryIds: [],
      },
      defaultCampaign: {
        name: '',
        description: '',
        shortDescription: '',
        premiumOnly: false,
        sendPushNotification: false,
        sendEmail: false,
        recommended: false,
        carouselCampaign: false,
        campaignKind: 'CAMPAIGN',
        mainCampaignId: null,
        carouselImageId: null,
        carouselImageUrl: '',
        carouselImageName: '',
        trackingLink: '',
        realLink: '',
        redirectLink: '',
        startDateTime: '',
        endDateTime: '',
        categoryIds: [],
      },
      mainCampaigns: [],
      categories: [],
      campaignKindOptions: [
        { text: 'Kampanya', value: 'CAMPAIGN' },
        { text: 'Koleksiyon', value: 'COLLECTION' },
      ],
      statusOptions: [
        { text: 'Aktif', value: 'ACTIVE' },
        { text: 'Planlanan', value: 'PLAN' },
        { text: 'Tamamlanmış', value: 'COMPLETED' },
        { text: 'Tümü', value: 'ALL' },
      ],
      campaignStatusOptions: [
        { text: 'Aktif', value: 'ACTIVE' },
        { text: 'Planlanan', value: 'PLAN' },
        { text: 'Tamamlanmış', value: 'COMPLETED' },
      ],
      carouselOptions: [
        { text: 'Tümü', value: 'ALL' },
        { text: 'Sadece Karosel', value: 'ONLY' },
        { text: 'Karosel Dışı', value: 'EXCLUDE' },
      ],
      selectedStatusFilter: 'ACTIVE',
      selectedCarouselFilter: 'ALL',
      icons: {
        mdiTableColumn,
        mdiClose,
        mdiPencil,
        mdiUpload,
        mdiDelete,
        mdiImage,
        mdiCrop,
        mdiLinkVariant,
        mdiOpenInNew,
      },
      requiredRules: [
        v => !!(v || v === 0) || 'Zorunlu alan',
      ],
      deleteDialog: false,
      deleteLoading: false,
      deleteError: '',
      deleteItem: {},
      editDialog: false,
      statusDialog: false,
      statusItem: {},
      statusError: '',
      statusLoading: false,
      toggleRecommended: false,
      togglePremium: false,
      toggleActive: false,
      editedCampaign: {
        id: 0,
        name: '',
        description: '',
        shortDescription: '',
        premiumOnly: false,
        recommended: false,
        carouselCampaign: false,
        campaignKind: 'CAMPAIGN',
        status: 'ACTIVE',
        promoteAsNew: false,
        mainCampaignId: null,
        carouselImageId: null,
        carouselImageUrl: '',
        carouselImageName: '',
        mainCampaignName: '',
        mainCampaignSourceName: '',
        mainCampaignPayout: null,
        mainCampaignPayoutType: null,
        trackingLink: '',
        realLink: '',
        redirectLink: '',
        startDateTime: '',
        endDateTime: '',
        categoryIds: [],
      },
      editedCampaignSnapshot: null,
    }
  },
  computed: {
    candidateDateComparison() {
      const evidence = this.candidate?.evidence || {}

      return [
        { label: 'Başlangıç', source: evidence.verifiedStartAt ? this.formatDateTimeInput(evidence.verifiedStartAt, true).replace('T', ' ') : '', current: (this.newCampaign.startDateTime || '').replace('T', ' ') },
        { label: 'Bitiş', source: evidence.verifiedEndAt ? this.formatDateTimeInput(evidence.verifiedEndAt, true).replace('T', ' ') : '', current: (this.newCampaign.endDateTime || '').replace('T', ' ') },
      ]
    },
    visibleHeaders() { return this.headers.filter(header => ['name', 'action'].includes(header.value) || this.visibleColumns.includes(header.value)) },
    candidateNavigationIndex() { return this.candidateNavigationIds.indexOf(this.candidate?.id) },
    composerReadonly() { return !!this.candidate && this.candidate.status !== 'PENDING' },
    candidateStatusLabel() {
      return { PENDING: 'Onay bekliyor', APPROVED: 'Onaylandı', REJECTED: 'Reddedildi' }[this.candidate?.status] || ''
    },
    selectedCategoryNames() {
      return this.categories.filter(category => this.newCampaign.categoryIds.includes(category.id)).map(category => category.name).join(', ')
    },
    editedCategoryNames() {
      return this.categories.filter(category => this.editedCampaign.categoryIds.includes(category.id)).map(category => category.name).join(', ')
    },
    candidateMode() { return !this.carouselMode && this.campaignTab === 2 },
    candidateEvidence() { return JSON.stringify(this.candidate?.evidence || {}, null, 2) },
    expiryReviewMode() {
      return !this.carouselMode && this.campaignTab === 1
    },
    todayIstanbulInput() {
      const parts = this.getIstanbulDateParts(new Date())

      return `${parts.year}-${this.padDatePart(parts.month)}-${this.padDatePart(parts.day)}`
    },
    canManageUrlHealth() {
      return hasAdminAccess(getPanelSession())
    },
    pageEyebrow() {
      return this.carouselMode ? 'Karosel' : 'Kampanyalar'
    },
    pageTitle() {
      return this.carouselMode ? 'Karosel kampanyaları' : 'Adverport kampanyaları'
    },
    pageDescription() {
      return this.carouselMode
        ? 'Mobil keşfet karoselinde gösterilecek kampanyaları ayrı bir yüzeyden yönetin.'
        : 'Gerçek kampanya kayıtlarını, görsel havuzu ve durum aksiyonlarıyla birlikte aynı operasyon yüzeyinde yönetin.'
    },
    imageTotalPages() {
      if (this.imageTotalPagesOverride) return this.imageTotalPagesOverride

      return this.imageTotalElements === 0 ? 1 : Math.ceil(this.imageTotalElements / this.imageItemsPerPage)
    },
    carouselCropAspectRatio() {
      return this.cardCropEditor ? 4 / 3 : 35 / 18
    },
    cropReady() {
      return this.imageCropLoaded && this.cropMeta.naturalWidth > 0 && this.cropMeta.naturalHeight > 0
        && this.cropMeta.renderedWidth > 0 && this.cropMeta.renderedHeight > 0 && this.cropRect.width > 0 && this.cropRect.height > 0
    },
    cropSelectionStyle() {
      if (!this.cropReady) {
        return {}
      }

      return {
        left: `${this.cropRect.x}px`,
        top: `${this.cropRect.y}px`,
        width: `${this.cropRect.width}px`,
        height: `${this.cropRect.height}px`,
      }
    },
    cropPreviewImageStyle() {
      if (!this.cropReady) {
        return {}
      }

      const cropX = this.cropRect.x - this.cropMeta.offsetX
      const cropY = this.cropRect.y - this.cropMeta.offsetY

      return {
        width: `${(this.cropMeta.renderedWidth / this.cropRect.width) * 100}%`,
        height: `${(this.cropMeta.renderedHeight / this.cropRect.height) * 100}%`,
        left: `-${(cropX / this.cropRect.width) * 100}%`,
        top: `-${(cropY / this.cropRect.height) * 100}%`,
      }
    },
    selectedCreateOffer() {
      return this.mainCampaigns.find(item => String(item.id) === String(this.newCampaign.mainCampaignId)) || null
    },
    editOfferItems() {
      const items = [...this.mainCampaigns]
      const selectedId = this.editedCampaign.mainCampaignId
      if (!selectedId) return items

      if (items.some(item => String(item.id) === String(selectedId))) {
        return items
      }

      return [
        {
          id: selectedId,
          name: this.editedCampaign.mainCampaignName || `Network Offer #${selectedId}`,
          payout: this.editedCampaign.mainCampaignPayout,
          payoutType: this.editedCampaign.mainCampaignPayoutType,
          networkSource: this.editedCampaign.mainCampaignSourceName,
          active: 1,
        },
        ...items,
      ]
    },
  },
  watch: {
    carouselMode() {
      this.applyViewMode()
      this.loadTableData()
    },
    imageCropDialog(value) {
      if (value) {
        this.scheduleCropMeasurement(false)
      } else {
        this.clearCropMeasurementSchedule()
      }
    },
  },
  mounted() {
    this.applyViewMode()
    if (this.$route.query.tab === 'pending' && this.canManageUrlHealth) this.campaignTab = 2
    this.loadTableData()
    if (!this.carouselMode) {
      this.loadExpiryReviewData()
    }
  },
  beforeDestroy() {
    clearTimeout(this.searchTimeout)
    clearTimeout(this.expiryReviewSearchTimeout)
    this.clearCropMeasurementSchedule()
    this.detachCropDragListeners()
    window.removeEventListener('resize', this.handleCropViewportResize)
  },
  methods: {
    applySavedCampaignFilters(filters) {
      clearTimeout(this.searchTimeout)
      this.searchValue = filters.search || ''
      if (this.statusOptions.some(option => option.value === filters.status)) this.selectedStatusFilter = filters.status
      if (this.carouselOptions.some(option => option.value === filters.carousel)) this.selectedCarouselFilter = filters.carousel
      this.params.searchValue = this.searchValue
      this.params.page = 0
      this.loadTableData()
    },
    applyViewMode() {
      if (this.carouselMode) {
        this.campaignTab = 0
        this.selectedCarouselFilter = 'ONLY'
      } else if (this.selectedCarouselFilter === 'ONLY') {
        this.selectedCarouselFilter = 'ALL'
      }
    },
    onCampaignTabChange(tab) {
      if (tab === 2) return
      if (tab === 1) {
        this.expiryReviewParams.page = 0
        this.expiryReviewOptions = {
          ...this.expiryReviewOptions,
          page: 1,
        }
        this.loadExpiryReviewData()

        return
      }

      this.loadTableData()
    },
    onUpdateTableOptions(options) {
      if (!options || !options.page) return

      const {
        sortBy, sortDesc, page, itemsPerPage,
      } = options

      this.params.page = page - 1
      this.params.size = itemsPerPage || 10

      if (sortBy && sortBy.length > 0) {
        // eslint-disable-next-line prefer-destructuring
        this.params.sortBy = sortBy[0]
        this.params.sortDirection = sortDesc && sortDesc[0] ? 'DESC' : 'ASC'
      }

      this.loadTableData()
    },
    onUpdateExpiryReviewOptions(options) {
      if (!options || !options.page) return

      const {
        sortBy, sortDesc, page, itemsPerPage,
      } = options

      this.expiryReviewParams.page = page - 1
      this.expiryReviewParams.size = itemsPerPage || 10
      if (sortBy && sortBy.length > 0) {
        this.expiryReviewParams.sortBy = this.resolveCampaignSortField(sortBy[0])
        this.expiryReviewParams.sortDirection = sortDesc && sortDesc[0] ? 'DESC' : 'ASC'
      }

      this.loadExpiryReviewData()
    },
    onSearch() {
      clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(() => {
        this.params.searchValue = this.searchValue
        this.params.page = 0
        this.loadTableData()
      }, 250)
    },
    onExpiryReviewSearch() {
      clearTimeout(this.expiryReviewSearchTimeout)
      this.expiryReviewSearchTimeout = setTimeout(() => {
        this.expiryReviewParams.page = 0
        this.expiryReviewOptions = {
          ...this.expiryReviewOptions,
          page: 1,
        }
        this.loadExpiryReviewData()
      }, 250)
    },
    onStatusFilterChange() {
      this.params.page = 0
      this.loadTableData()
    },
    onCarouselFilterChange() {
      this.params.page = 0
      this.loadTableData()
    },
    resolveCampaignStatuses() {
      if (!this.selectedStatusFilter || this.selectedStatusFilter === 'ALL') {
        return undefined
      }

      return [this.selectedStatusFilter]
    },
    resolveCarouselFilter() {
      if (this.selectedCarouselFilter === 'ONLY') {
        return true
      }
      if (this.selectedCarouselFilter === 'EXCLUDE') {
        return false
      }

      return undefined
    },
    async loadTableData() {
      this.loading = true
      this.tableError = ''
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_BASE_API_URL}campaign/listForAdminV2`,
          {
            ...this.requestOptions,
            params: {
              statusList: this.resolveCampaignStatuses(),
              carouselCampaign: this.resolveCarouselFilter(),
              search: this.params.searchValue || undefined,
              page: this.params.page,
              size: this.params.size,
              sortBy: this.resolveCampaignSortField(this.params.sortBy),
              direction: this.params.sortDirection,
            },
            paramsSerializer: serializeParams,
          },
        )

        if (response.data.status === 200 && response.data.data) {
          const page = response.data.data
          this.campaigns = (page.content || []).map(this.normalizeCampaign)
          this.totalElements = page.totalElements || 0
        }
      } catch (error) {
        console.error('Kampanyalar yüklenirken hata oluştu:', error)
        if (isAuthError(error)) {
          this.$router.push({ name: 'login' })

          return
        }

        this.campaigns = []
        this.totalElements = 0
        this.tableError = resolveApiErrorMessage(error, 'Adverport kampanyaları yüklenemedi.')
      } finally {
        this.loading = false
      }
    },
    async loadExpiryReviewData() {
      if (this.carouselMode) return

      this.expiryReviewLoading = true
      this.expiryReviewError = ''
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_BASE_API_URL}campaign/expiry-review`,
          {
            ...this.requestOptions,
            params: {
              search: this.expiryReviewSearch || undefined,
              page: this.expiryReviewParams.page,
              size: this.expiryReviewParams.size,
              sortBy: this.expiryReviewParams.sortBy,
              direction: this.expiryReviewParams.sortDirection,
            },
          },
        )
        const page = response?.data?.data || {}

        this.expiryReviewCampaigns = (page.content || []).map(this.normalizeCampaign)
        this.expiryReviewTotal = page.totalElements || 0
      } catch (error) {
        console.error('Bitiş kontrolü kampanyaları yüklenirken hata oluştu:', error)
        if (isAuthError(error)) {
          this.$router.push({ name: 'login' })

          return
        }

        this.expiryReviewCampaigns = []
        this.expiryReviewTotal = 0
        this.expiryReviewError = resolveApiErrorMessage(error, 'Bitiş kontrolü listesi yüklenemedi.')
      } finally {
        this.expiryReviewLoading = false
      }
    },
    normalizeCampaign(item) {
      const proposal = item.proposal ?? item.recommended ?? false
      const realLink = item.realLink || ''

      return {
        ...item,
        recommended: proposal,
        proposal,
        carouselCampaign: !!(item.carouselCampaign ?? item.carousel ?? false),
        status: this.normalizeCampaignStatusValue(item.status),
        promoteAsNew: false,
        logoUrl: item.logoUrl || item.company?.logo?.localUrl || item.logo?.localUrl || '',
        trackingLink: realLink,
        realLink,
        redirectLink: item.redirectLink || '',
        startDateTime: item.startDateTime || item.activationDate || '',
        endDateTime: item.endDateTime || item.endDate || '',
        mainCampaignId: item.offerRefId || null,
        mainCampaignName: item.company?.name || item.mainCampaignName || '',
        mainCampaignOfferId: item.mainCampaignOfferId || item.offerId || item.offerRefId || '-',
        mainCampaignSourceName: item.mainCampaignSourceName || item.networkSource || '-',
        payout: item.commissionValue ?? null,
        payoutType: item.commissionType || null,
        mainCampaignPayout: item.commissionValue ?? null,
        mainCampaignPayoutType: item.commissionType || null,
        campaignKind: (item.type || item.campaignKind || 'CAMPAIGN').toUpperCase(),
        carouselImageId: item.carouselImageId || null,
        carouselImageUrl: item.carouselImageUrl || '',
        carouselImageName: this.resolveCarouselImageName(item),
        categoriesString: item.categoriesString || '',
        categoryIds: this.resolveCategoryIds(item),
      }
    },
    formatDate(timestamp) {
      if (!timestamp) return '-'
      const parts = this.parseCampaignDateParts(timestamp)
      if (!parts) return timestamp

      return `${this.padDatePart(parts.day)}.${this.padDatePart(parts.month)}.${parts.year}`
    },
    formatDateTime(value) {
      if (!value) return '-'
      const parts = this.parseCampaignDateParts(value)
      if (!parts) return value

      return `${this.padDatePart(parts.day)}.${this.padDatePart(parts.month)}.${parts.year} ${this.padDatePart(parts.hour)}:${this.padDatePart(parts.minute)}`
    },
    formatNullableDateTime(value) {
      if (!value) return ''

      return this.formatDateTime(value)
    },
    formatDateTimeInput(value, includeSeconds = false) {
      if (!value) return ''
      const parts = this.parseCampaignDateParts(value)
      if (!parts) return ''

      return `${parts.year}-${this.padDatePart(parts.month)}-${this.padDatePart(parts.day)}T${this.padDatePart(parts.hour)}:${this.padDatePart(parts.minute)}${includeSeconds ? `:${this.padDatePart(parts.second)}` : ''}`
    },
    formatExpiryReviewDateInput(value) {
      const parts = this.parseCampaignDateParts(value)
      if (!parts) return ''

      return `${parts.year}-${this.padDatePart(parts.month)}-${this.padDatePart(parts.day)}`
    },
    getExpiryReviewDays(value) {
      const endParts = this.parseCampaignDateParts(value)
      if (!endParts) return null
      const todayParts = this.getIstanbulDateParts(new Date())
      const endDay = Date.UTC(endParts.year, endParts.month - 1, endParts.day)
      const today = Date.UTC(todayParts.year, todayParts.month - 1, todayParts.day)

      return Math.round((endDay - today) / 86400000)
    },
    getExpiryReviewLabel(value) {
      const days = this.getExpiryReviewDays(value)
      if (days === null) return '-'
      if (days < 0) return `${Math.abs(days)} gün gecikti`
      if (days === 0) return 'Bugün bitiyor'
      if (days === 1) return 'Yarın bitiyor'

      return `${days} gün kaldı`
    },
    getExpiryReviewColor(value) {
      const days = this.getExpiryReviewDays(value)
      if (days === null) return 'secondary'
      if (days < 0) return 'error'
      if (days <= 1) return 'warning'

      return 'info'
    },
    formatNumber(val) {
      if (val === null || val === undefined) return '-'
      const num = Number(val)
      if (Number.isNaN(num)) return val

      return num.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
    formatCampaignKind(value) {
      const normalized = String(value || 'CAMPAIGN').trim().toUpperCase()

      return normalized === 'COLLECTION' ? 'Koleksiyon' : 'Kampanya'
    },
    getCampaignStatusLabel(status) {
      const normalized = this.normalizeCampaignStatusValue(status)
      if (normalized === 'PLAN') return 'Plan'
      if (normalized === 'COMPLETED') return 'Tamamlanmış'

      return 'Aktif'
    },
    getCampaignStatusColor(status) {
      const normalized = this.normalizeCampaignStatusValue(status)
      if (normalized === 'PLAN') return 'warning'
      if (normalized === 'COMPLETED') return 'secondary'

      return 'success'
    },
    normalizeCampaignStatusValue(status) {
      const normalized = String(status || 'ACTIVE').trim().toUpperCase()
      if (normalized === 'PLAN') return 'PLAN'
      if (normalized === 'ACTIVE') return 'ACTIVE'

      return 'COMPLETED'
    },
    formatNetworkPayout(item) {
      const payoutValue = item?.mainCampaignPayout
      if (payoutValue === null || payoutValue === undefined || payoutValue === '') return '-'
      const payoutText = this.formatNumber(payoutValue)
      const payoutType = String(item?.mainCampaignPayoutType || item?.payoutType || '').trim().toLowerCase()
      if (payoutType.includes('percentage')) {
        return `%${payoutText}`
      }

      return `${payoutText} TRY`
    },
    formatCategories(categories, categoriesString = '') {
      if (Array.isArray(categories) && categories.length > 0) {
        return categories.map(cat => cat?.name || cat?.label || '-').join(', ')
      }
      if (categoriesString && categoriesString.trim()) return categoriesString

      return '-'
    },
    resolveCategoryIds(source, availableCategories = this.categories) {
      if (!source) return []
      if (Array.isArray(source.categoryIds) && source.categoryIds.length > 0) {
        return source.categoryIds
          .map(id => Number(id))
          .filter(id => !Number.isNaN(id))
      }

      if (Array.isArray(source.categories) && source.categories.length > 0) {
        const directIds = source.categories
          .map(category => category?.id || category)
          .map(id => Number(id))
          .filter(id => !Number.isNaN(id))

        if (directIds.length > 0) return directIds

        if (availableCategories.length > 0) {
          const names = source.categories
            .map(category => String(category?.name || category || '').trim().toLowerCase())
            .filter(Boolean)

          return availableCategories
            .filter(category => names.includes(String(category?.name || '').trim().toLowerCase()))
            .map(category => Number(category.id))
        }
      }

      if (source.categoriesString && availableCategories.length > 0) {
        const names = source.categoriesString
          .split(',')
          .map(name => name.trim().toLowerCase())
          .filter(Boolean)

        return availableCategories
          .filter(category => names.includes(String(category?.name || '').trim().toLowerCase()))
          .map(category => Number(category.id))
      }

      return []
    },
    async ensureCreateDialogData(force = false) {
      if (!force && this.mainCampaigns.length > 0 && this.categories.length > 0) return

      this.createDialogBootstrapping = true
      try {
        const results = await Promise.allSettled([
          this.loadMainCampaigns(force),
          this.loadCategories(force),
        ])

        const firstRejected = results.find(result => result.status === 'rejected')
        if (firstRejected) {
          throw firstRejected.reason
        }
      } finally {
        this.createDialogBootstrapping = false
      }
    },
    async loadMainCampaigns(force = false) {
      if (!force && this.mainCampaigns.length > 0) return this.mainCampaigns
      if (this.offerRequest) return this.offerRequest

      this.offerLoading = true
      this.offerRequest = axios.get(
        `${process.env.VUE_APP_BASE_API_URL}panel/offers`,
        {
          ...this.requestOptions,
          params: {
            page: 0,
            size: 5000,
            sort: 'name,asc',
          },
          timeout: 12000,
        },
      )
        .then(response => {
          if (response.data.status === 200 && response.data.data) {
            const payload = response.data.data
            const offerList = Array.isArray(payload) ? payload : (payload.content || [])
            this.mainCampaigns = offerList
              .filter(item => Number(item.active) === 1)
              .map(item => ({
                id: item.offerRefId || item.id,
                name: item.name,
                payout: item.payout ?? item.campaigns?.[0]?.payout ?? null,
                payoutType: item.payoutType || item.campaigns?.[0]?.payoutType || null,
                networkSource: item.networkSource,
                active: item.active,
              }))
          }

          return this.mainCampaigns
        })
        .catch(error => {
          console.error('Offer listesi yüklenirken hata oluştu:', error)
          if (!this.mainCampaigns.length) {
            throw error
          }

          return this.mainCampaigns
        })
        .finally(() => {
          this.offerLoading = false
          this.offerRequest = null
        })

      return this.offerRequest
    },
    async loadCategories(force = false) {
      if (!force && this.categories.length > 0) return this.categories
      if (this.categoryRequest) return this.categoryRequest

      this.categoryLoading = true
      this.categoryRequest = axios.get(
        `${process.env.VUE_APP_BASE_API_URL}category`,
        {
          ...this.requestOptions,
          timeout: 12000,
        },
      )
        .then(response => {
          if ((response.data.status === 200 || response.data.status === 0) && response.data.data) {
            const payload = response.data.data
            this.categories = (Array.isArray(payload) ? payload : (payload.content || payload.list || []))
              .map(category => ({
                ...category,
                id: Number(category.id),
              }))
              .filter(category => !Number.isNaN(category.id))
          }

          return this.categories
        })
        .catch(error => {
          console.error('Kategoriler yüklenirken hata oluştu:', error)
          if (!this.categories.length) {
            throw error
          }

          return this.categories
        })
        .finally(() => {
          this.categoryLoading = false
          this.categoryRequest = null
        })

      return this.categoryRequest
    },
    async openCreateDialog() {
      if (this.saveLoading || this.editorImageUploading) return
      this.candidate = null
      this.createError = ''
      this.editSuccess = ''
      this.editorImageUploading = false
      if (this.carouselMode) {
        this.newCampaign.carouselCampaign = true
      }
      this.createDialog = true
      try {
        await this.ensureCreateDialogData()
      } catch (error) {
        this.createError = resolveApiErrorMessage(error, 'Offer veya kategori listesi yüklenemedi.')
      }
    },
    closeCreate() {
      if (this.saveLoading || this.editorImageUploading) return
      this.createDialog = false

      setTimeout(() => {
        if (this.createDialog) return
        this.createError = ''
        this.editSuccess = ''
        this.editorImageUploading = false
        this.candidate = null
        this.newCampaign = {
          ...this.defaultCampaign,
          carouselCampaign: this.carouselMode,
        }
      }, 300)
    },
    async saveCreate() {
      if (this.saveLoading || this.editorImageUploading) return
      if (campaignActivationInFlight) {
        this.createError = 'Önceki kampanya aktivasyonu devam ediyor. Tamamlanınca tekrar deneyin.'

        return
      }
      this.createError = ''
      this.campaignSaveSuccess = ''
      const validationError = this.validateCreateCampaign()

      if (validationError) {
        this.createError = validationError

        return
      }

      this.saveLoading = true
      try {
        const activationDate = this.toBackendDateTime(this.newCampaign.startDateTime)
        const endDate = this.toBackendCampaignEndDate(this.newCampaign.endDateTime)
        const form = {
          name: this.newCampaign.name.trim(),
          description: this.getEditorHtml('createDescriptionEditor'),
          shortDescription: this.newCampaign.shortDescription.trim(),
          offerRefId: String(this.newCampaign.mainCampaignId),
          categoryId: this.newCampaign.categoryIds.map(id => String(id)),
          premiumOnly: this.newCampaign.premiumOnly,
          sendPushNotification: this.newCampaign.sendPushNotification,
          sendEmail: this.newCampaign.sendEmail,
          carouselCampaign: this.newCampaign.carouselCampaign,
          carouselImageId: this.newCampaign.carouselImageId || null,
          proposal: this.newCampaign.recommended,
          campaignKind: this.newCampaign.campaignKind,
          activationDate,
          endDate,
          link: this.newCampaign.trackingLink.trim(),
        }

        // Keep the draft intact until the activation transaction acknowledges success.
        campaignActivationInFlight = true
        this.activationPending = true
        this.activationPendingName = form.name
        this.createDialog = false
        await axios.post(
          `${process.env.VUE_APP_BASE_API_URL}${this.candidate ? `admin/campaign-candidates/${this.candidate.id}/approve` : 'campaign/create'}`,
          this.candidate ? { version: this.candidate.version, sourceReviewed: this.candidateReviewed, form } : form,
          this.requestOptions,
        )
        this.saveLoading = false
        this.closeCreate()
        this.campaignSaveSuccess = 'Kampanya kaydedildi.'

        // The save has committed. Refresh failures must not look like a failed create.
        this.refreshAfterCreate()
      } catch (error) {
        console.error('Kampanya oluşturulurken hata oluştu:', error)
        this.createDialog = true
        this.createError = resolveApiErrorMessage(error, error.response
          ? 'Kampanya oluşturulamadı.'
          : 'İşlem sonucu alınamadı. Yeniden onaylamadan önce listeyi yenileyip kampanyanın oluşup oluşmadığını kontrol edin.')
      } finally {
        campaignActivationInFlight = false
        this.activationPending = false
        this.activationPendingName = ''
        this.saveLoading = false
      }
    },
    async refreshAfterCreate() {
      try {
        await Promise.all([this.loadTableData(), this.$refs.candidateQueue?.load()])
      } catch (error) {
        this.tableError = resolveApiErrorMessage(error, 'Kampanya kaydedildi; liste yenilenemedi. Yenile ile tekrar deneyin.')
      }
    },
    async openCandidate(item) {
      if (this.createDialogBootstrapping || this.saveLoading) return
      if (!this.createDialog) this.candidateNavigationIds = (this.$refs.candidateQueue?.visibleRows || []).map(row => row.id)
      this.createError = ''
      this.createDialog = true
      this.createDialogBootstrapping = true
      try {
        const response = await axios.get(`${process.env.VUE_APP_BASE_API_URL}admin/campaign-candidates/${item.id}`, this.requestOptions)
        this.candidate = response.data.data
        this.candidateReviewed = this.candidate.sourceReviewed
        await this.ensureCreateDialogData()
        const { form } = this.candidate
        this.newCampaign = {
          ...this.defaultCampaign,
          ...form,
          mainCampaignId: Number(form.offerRefId),
          trackingLink: form.link || '',
          categoryIds: (form.categoryId || []).map(Number),
          startDateTime: this.formatDateTimeInput(form.activationDate, true),
          endDateTime: this.formatDateTimeInput(form.endDate, true),
          recommended: form.proposal,
        }
      } catch (error) {
        this.createDialog = false
        this.$refs.candidateQueue.error = resolveApiErrorMessage(error, 'Aday yüklenemedi.')
      } finally {
        this.createDialogBootstrapping = false
        this.$nextTick(() => {
          const editor = this.$refs.createDescriptionEditor
          if (editor?.quill) editor.quill.blur()
          const dialog = editor?.$el?.closest('.v-dialog')
          if (dialog) dialog.scrollTop = 0
        })
      }
    },
    candidateForm() {
      return {
        name: this.newCampaign.name,
        shortDescription: this.newCampaign.shortDescription,
        description: this.getEditorHtml('createDescriptionEditor'),
        offerRefId: String(this.newCampaign.mainCampaignId),
        categoryId: this.newCampaign.categoryIds.map(String),
        link: this.newCampaign.trackingLink,
        activationDate: this.toBackendDateTime(this.newCampaign.startDateTime),
        endDate: this.toBackendCampaignEndDate(this.newCampaign.endDateTime),
        campaignKind: this.newCampaign.campaignKind,
        premiumOnly: this.newCampaign.premiumOnly,
        proposal: this.newCampaign.recommended,
        carouselCampaign: this.newCampaign.carouselCampaign,
        carouselImageId: this.newCampaign.carouselImageId || null,
        sendEmail: this.newCampaign.sendEmail,
        sendPushNotification: this.newCampaign.sendPushNotification,
      }
    },
    async saveCandidateDraft() {
      if (this.saveLoading || this.editorImageUploading) return false
      this.saveLoading = true
      this.createError = ''
      try {
        const response = await axios.put(`${process.env.VUE_APP_BASE_API_URL}admin/campaign-candidates/${this.candidate.id}`, {
          version: this.candidate.version, sourceReviewed: this.candidateReviewed, form: this.candidateForm(),
        }, this.requestOptions)
        this.candidate = response.data.data
        await this.$refs.candidateQueue?.load()

        return true
      } catch (error) {
        this.createError = resolveApiErrorMessage(error, 'Taslak kaydedilemedi.')

        return false
      } finally { this.saveLoading = false }
    },
    async navigateCandidate(direction) {
      if (this.saveLoading || this.editorImageUploading || this.createDialogBootstrapping) return
      const nextId = this.candidateNavigationIds[this.candidateNavigationIndex + direction]
      if (!nextId) return
      if (this.candidate.status === 'PENDING' && !await this.saveCandidateDraft()) return
      await this.openCandidate({ id: nextId })
    },
    async rejectCandidate() {
      if (this.saveLoading) return
      this.saveLoading = true
      try {
        await axios.post(`${process.env.VUE_APP_BASE_API_URL}admin/campaign-candidates/${this.candidate.id}/reject`, { version: this.candidate.version }, this.requestOptions)
        await this.$refs.candidateQueue?.load()
        this.saveLoading = false
        this.closeCreate()
      } catch (error) { this.createError = resolveApiErrorMessage(error, 'Aday reddedilemedi.') } finally { this.saveLoading = false }
    },
    async editItem(item) {
      if (this.saveLoading || this.editorImageUploading) return
      this.editError = ''
      this.editSuccess = ''
      this.editorImageUploading = false
      this.editDialogBootstrapping = true
      this.editedCampaign = this.toCampaignForm(item)
      this.editedCampaignSnapshot = this.cloneCampaignForm(this.editedCampaign)
      this.editDialog = true
      try {
        await this.ensureCreateDialogData()
        this.editedCampaign = {
          ...this.editedCampaign,
          categoryIds: this.resolveCategoryIds(item),
        }
        this.editedCampaignSnapshot = this.cloneCampaignForm(this.editedCampaign)
      } catch (error) {
        this.editError = resolveApiErrorMessage(error, 'Offer veya kategori listesi yüklenemedi.')
      } finally {
        this.editDialogBootstrapping = false
      }
    },
    toCampaignForm(item) {
      return {
        id: item.id,
        name: item.name,
        description: item.description,
        shortDescription: item.shortDescription || '',
        premiumOnly: item.premiumOnly,
        promoteAsNew: false,
        carouselCampaign: !!(item.carouselCampaign ?? item.carousel ?? false),
        recommended: item.proposal ?? item.recommended ?? item.trend ?? false,
        campaignKind: (item.campaignKind || item.type || 'CAMPAIGN').toUpperCase(),
        status: this.normalizeCampaignStatusValue(item.status),
        trackingLink: item.realLink || '',
        realLink: item.realLink || '',
        redirectLink: item.redirectLink || '',
        carouselImageId: item.carouselImageId || null,
        carouselImageUrl: item.carouselImageUrl || '',
        carouselImageName: item.carouselImageName || this.resolveCarouselImageName(item),
        startDateTime: this.formatDateTimeInput(item.startDateTime, true),
        endDateTime: this.formatDateTimeInput(item.endDateTime, true),
        mainCampaignId: item.mainCampaignId,
        mainCampaignName: item.mainCampaignName || item.company?.name || '',
        mainCampaignSourceName: item.mainCampaignSourceName || item.networkSource || '',
        mainCampaignPayout: item.mainCampaignPayout ?? item.payout ?? null,
        mainCampaignPayoutType: item.mainCampaignPayoutType || item.payoutType || null,
        categoryIds: this.resolveCategoryIds(item),
      }
    },
    cloneCampaignForm(form) {
      return JSON.parse(JSON.stringify(form || {}))
    },
    normalizeComparableString(value) {
      return String(value ?? '').trim()
    },
    normalizeComparableRichText(value) {
      return this.normalizeComparableString(value)
        .replace(/<p><br><\/p>/g, '')
        .replace(/\s+/g, ' ')
    },
    normalizeComparableDateTime(value) {
      if (!value) return ''
      const parts = this.parseCampaignDateParts(value)
      if (!parts) return this.normalizeComparableString(value)

      return `${parts.year}-${this.padDatePart(parts.month)}-${this.padDatePart(parts.day)}T${this.padDatePart(parts.hour)}:${this.padDatePart(parts.minute)}:${this.padDatePart(parts.second)}`
    },
    normalizeComparableIds(value) {
      return (Array.isArray(value) ? value : [])
        .map(item => Number(item))
        .filter(item => Number.isFinite(item))
        .sort((left, right) => left - right)
        .join(',')
    },
    hasComparableChanged(before, after, normalizer) {
      const normalize = normalizer || this.normalizeComparableString

      return normalize.call(this, before) !== normalize.call(this, after)
    },
    buildCampaignUpdateSuccessMessage(before, after) {
      if (!before) return 'Kampanya güncellendi.'

      const changedLabels = []
      const addIfChanged = (label, beforeValue, afterValue, normalizer) => {
        if (this.hasComparableChanged(beforeValue, afterValue, normalizer)) {
          changedLabels.push(label)
        }
      }

      addIfChanged('kampanya adı', before.name, after.name)
      addIfChanged('network offer', before.mainCampaignId, after.mainCampaignId, value => String(value ?? ''))
      addIfChanged('tip', before.campaignKind, after.campaignKind)
      addIfChanged('durum', before.status, after.status)
      addIfChanged('kampanya linki', before.realLink, after.realLink)
      addIfChanged('aktif olma tarihi', before.startDateTime, after.startDateTime, this.normalizeComparableDateTime)
      addIfChanged('bitiş tarihi', before.endDateTime, after.endDateTime, this.normalizeComparableDateTime)
      addIfChanged('kategori', before.categoryIds, after.categoryIds, this.normalizeComparableIds)
      addIfChanged('önizleme metni', before.shortDescription, after.shortDescription)
      addIfChanged('detay açıklaması', before.description, after.description, this.normalizeComparableRichText)
      addIfChanged('önerilen kampanya', before.recommended, after.recommended, value => String(!!value))
      addIfChanged('keşfet karoseli', before.carouselCampaign, after.carouselCampaign, value => String(!!value))
      addIfChanged('premium kampanya', before.premiumOnly, after.premiumOnly, value => String(!!value))
      addIfChanged('karosel görseli', before.carouselImageId, after.carouselImageId, value => String(value ?? ''))
      if (after.promoteAsNew) {
        changedLabels.push('Yeniler ve karosel sırası')
      }

      if (changedLabels.length === 0) {
        return 'Kampanya güncellendi.'
      }

      const realLinkChanged = changedLabels.includes('kampanya linki')
      const fieldText = changedLabels.join(', ')

      return realLinkChanged
        ? `Kampanya güncellendi. Güncellenen alanlar: ${fieldText}. Redirect link yeniden oluşturuldu.`
        : `Kampanya güncellendi. Güncellenen alanlar: ${fieldText}.`
    },
    resolveCarouselImageName(item) {
      if (!item) return ''
      if (item.carouselImageName) return item.carouselImageName
      if (item.carouselImage?.name) return item.carouselImage.name
      if (item.name && item.localUrl && !item.company) return item.name
      if (item.carouselImageUrl) {
        return item.carouselImageUrl.split('/').pop()
      }

      return ''
    },
    formatPayoutSummary(offer) {
      if (!offer) return '-'
      const payoutValue = offer.payout ?? offer.mainCampaignPayout
      if (payoutValue === null || payoutValue === undefined || payoutValue === '') return '-'
      const suffix = offer.payoutType ? ` ${offer.payoutType}` : ''

      return `${this.formatNumber(payoutValue)}${suffix}`
    },
    validateCreateCampaign() {
      if (!this.newCampaign.mainCampaignId) return 'Offer seçimi zorunlu.'
      if (!this.newCampaign.name || !this.newCampaign.name.trim()) return 'Kampanya adı zorunlu.'
      if (this.newCampaign.name.length > 255) return 'Kampanya adı en fazla 255 karakter olabilir.'
      if (!this.newCampaign.trackingLink || !this.newCampaign.trackingLink.trim()) return 'Kampanya linki zorunlu.'
      if (!this.newCampaign.endDateTime) return 'Bitiş tarihi zorunlu.'
      if (!Array.isArray(this.newCampaign.categoryIds) || this.newCampaign.categoryIds.length === 0) return 'En az bir kategori seçin.'
      if (!this.newCampaign.shortDescription || !this.newCampaign.shortDescription.trim()) return 'Kampanya önizleme metni zorunlu.'
      if (!this.hasRichTextContent(this.newCampaign.description)) return 'Kampanya detay açıklaması zorunlu.'

      if (this.newCampaign.startDateTime && this.compareCampaignDateTimeValues(this.newCampaign.endDateTime, this.newCampaign.startDateTime) <= 0) {
        return 'Bitiş tarihi aktif olma tarihinden sonra olmalı.'
      }

      return ''
    },
    hasRichTextContent(value) {
      return this.stripHtml(value).length > 0
    },
    stripHtml(value) {
      return (value || '')
        .replace(/<(.|\n)*?>/g, ' ')
        .replace(/&nbsp;/gi, ' ')
        .replace(/\s+/g, ' ')
        .trim()
    },
    getEditorHtml(refName) {
      this.normalizeEditorLinks(refName)

      const editor = this.$refs[refName]?.quill
      if (!editor) {
        return refName === 'editDescriptionEditor'
          ? this.editedCampaign.description
          : this.newCampaign.description
      }

      return editor.root.innerHTML
    },
    normalizeEditorLinks(refName) {
      const editor = this.$refs[refName]?.quill
      if (!editor) return

      const links = editor.root.querySelectorAll('a')
      links.forEach(link => {
        const href = link.getAttribute('href')
        if (href && !href.startsWith('http://') && !href.startsWith('https://')) {
          link.setAttribute('href', `https://${href}`)
        }
      })
    },
    async handleDescriptionImageUpload(refName) {
      const input = document.createElement('input')

      input.type = 'file'
      input.accept = 'image/*'
      input.click()

      input.onchange = async () => {
        const file = input.files?.[0]
        if (!file) return

        this.editorImageUploading = true
        try {
          const formData = new FormData()
          formData.append('file', file)
          formData.append('folder', 'cdn.adverport.com/adverportaffiliate/campaigns/description-images')
          const response = await axios.post(
            `${process.env.VUE_APP_BASE_API_URL2}file/uploadFile`,
            formData,
            {
              headers: {
                Authorization: `Bearer ${getAccessToken()}`,
                'Content-Type': 'multipart/form-data',
              },
            },
          )

          const imageUrl = response?.data?.localUrl
            || response?.data?.data?.localUrl
            || response?.data?.url
            || response?.data?.data?.url

          if (!imageUrl) {
            throw new Error('Görsel URL alınamadı.')
          }

          const editor = this.$refs[refName]?.quill
          if (!editor) return
          const range = editor.getSelection(true) || { index: editor.getLength(), length: 0 }

          editor.insertEmbed(range.index, 'image', imageUrl, 'user')
          editor.setSelection(range.index + 1, 0)
        } catch (error) {
          console.error('Editör görseli yüklenirken hata oluştu:', error)
          const message = resolveApiErrorMessage(error, 'Editör görseli yüklenemedi.')
          if (refName === 'editDescriptionEditor') {
            this.editError = message
          } else {
            this.createError = message
          }
        } finally {
          this.editorImageUploading = false
        }
      }
    },
    closeEdit() {
      if (this.saveLoading || this.editorImageUploading) return
      this.editDialog = false
      setTimeout(() => {
        this.editError = ''
        this.editSuccess = ''
        this.editorImageUploading = false
        this.editedCampaignSnapshot = null
      }, 300)
    },
    async saveEdit() {
      if (this.editDialogBootstrapping || this.saveLoading || this.editorImageUploading) return
      this.editError = ''
      this.editSuccess = ''
      const editPayloadSource = {
        ...this.editedCampaign,
        description: this.getEditorHtml('editDescriptionEditor'),
        proposal: this.editedCampaign.recommended,
        carouselCampaign: this.editedCampaign.carouselCampaign,
      }
      const validationError = this.validateEditCampaign()

      if (validationError) {
        this.editError = validationError

        return
      }

      this.saveLoading = true
      try {
        const response = await axios.put(
          `${process.env.VUE_APP_BASE_API_URL}campaign/update/${this.editedCampaign.id}`,
          this.buildCampaignUpdatePayload(editPayloadSource),
          this.requestOptions,
        )
        const successMessage = this.buildCampaignUpdateSuccessMessage(this.editedCampaignSnapshot, editPayloadSource)

        if (response?.data?.data) {
          this.editedCampaign = this.toCampaignForm(this.normalizeCampaign(response.data.data))
          this.editedCampaignSnapshot = this.cloneCampaignForm(this.editedCampaign)
        }
        await this.loadTableData()
        this.editSuccess = successMessage
      } catch (error) {
        console.error('Kampanya güncellenirken hata oluştu:', error)
        this.editError = resolveApiErrorMessage(error, 'Kampanya güncellenemedi.')
      } finally {
        this.saveLoading = false
      }
    },
    validateEditCampaign() {
      if (!this.editedCampaign.mainCampaignId) return 'Offer seçimi zorunlu.'
      if (!this.editedCampaign.name || !this.editedCampaign.name.trim()) return 'Kampanya adı zorunlu.'
      if (this.editedCampaign.name.length > 255) return 'Kampanya adı en fazla 255 karakter olabilir.'
      if (!this.editedCampaign.realLink || !this.editedCampaign.realLink.trim()) return 'Kampanya linki zorunlu.'
      if (!this.editedCampaign.endDateTime) return 'Bitiş tarihi zorunlu.'
      if (!Array.isArray(this.editedCampaign.categoryIds) || this.editedCampaign.categoryIds.length === 0) return 'En az bir kategori seçin.'
      if (!this.editedCampaign.shortDescription || !this.editedCampaign.shortDescription.trim()) return 'Kampanya önizleme metni zorunlu.'
      if (!this.hasRichTextContent(this.editedCampaign.description)) return 'Kampanya detay açıklaması zorunlu.'

      if (this.editedCampaign.startDateTime && this.compareCampaignDateTimeValues(this.editedCampaign.endDateTime, this.editedCampaign.startDateTime) <= 0) {
        return 'Bitiş tarihi aktif olma tarihinden sonra olmalı.'
      }
      if (this.editedCampaign.status === 'ACTIVE' && this.isPastCampaignEndDate(this.editedCampaign.endDateTime)) {
        return this.pastCampaignEndDateActivationMessage()
      }

      return ''
    },
    openStatusDialog(item) {
      this.statusItem = item
      this.statusError = ''
      this.toggleActive = item.status === 'ACTIVE'
      this.toggleRecommended = !!(item.proposal ?? item.recommended ?? item.trend)
      this.togglePremium = !!item.premiumOnly
      this.statusDialog = true
    },
    closeStatusDialog() {
      this.statusDialog = false
      this.statusItem = {}
      this.statusError = ''
      this.statusLoading = false
    },
    async updateStatus() {
      if (!this.statusItem?.id) return
      const status = this.toggleActive ? 'ACTIVE' : 'COMPLETED'
      this.statusError = ''
      if (status === 'ACTIVE' && this.isPastCampaignEndDate(this.statusItem.endDateTime || this.statusItem.endDate)) {
        this.statusError = this.pastCampaignEndDateActivationMessage()
        this.toggleActive = false

        return
      }
      this.statusLoading = true
      try {
        await axios.put(
          `${process.env.VUE_APP_BASE_API_URL}campaign/status/${this.statusItem.id}?status=${status}`,
          {},
          this.requestOptions,
        )
        this.loadTableData()
      } catch (error) {
        console.error('Durum güncellenirken hata oluştu:', error)
        this.statusError = resolveApiErrorMessage(error, 'Kampanya durumu güncellenemedi.')
        this.toggleActive = this.statusItem.status === 'ACTIVE'
      } finally {
        this.statusLoading = false
      }
    },
    async updateRecommended() {
      if (!this.statusItem?.id) return
      this.statusLoading = true
      try {
        await axios.put(
          `${process.env.VUE_APP_BASE_API_URL}campaign/update/${this.statusItem.id}`,
          this.buildCampaignUpdatePayload({
            ...this.statusItem,
            proposal: this.toggleRecommended,
            recommended: this.toggleRecommended,
          }),
          this.requestOptions,
        )
        this.loadTableData()
      } catch (error) {
        console.error('Önerilen güncellenirken hata oluştu:', error)
      } finally {
        this.statusLoading = false
      }
    },
    async updatePremiumOnly() {
      if (!this.statusItem?.id) return
      this.statusLoading = true
      try {
        await axios.put(
          `${process.env.VUE_APP_BASE_API_URL}campaign/update/${this.statusItem.id}`,
          this.buildCampaignUpdatePayload({
            ...this.statusItem,
            premiumOnly: this.togglePremium,
          }),
          this.requestOptions,
        )
        this.loadTableData()
      } catch (error) {
        console.error('Premium durumu güncellenirken hata oluştu:', error)
      } finally {
        this.statusLoading = false
      }
    },
    openCampaignWebsite(item) {
      if (!item?.realLink) return
      const openedWindow = window.open(item.realLink, '_blank', 'noopener,noreferrer')
      if (openedWindow) openedWindow.opener = null
    },
    isExpiryReviewActionLoading(id, action) {
      return this.expiryReviewAction === `${id}:${action}`
    },
    isExpiryReviewItemBusy(id) {
      return this.expiryReviewAction.startsWith(`${id}:`)
    },
    async extendExpiryReview(item) {
      if (!item?.id || this.isExpiryReviewItemBusy(item.id)) return

      this.expiryReviewAction = `${item.id}:extend`
      this.expiryReviewError = ''
      this.expiryReviewSuccess = ''
      try {
        const response = await axios.put(
          `${process.env.VUE_APP_BASE_API_URL}campaign/expiry-review/${item.id}/extend-one-day`,
          {},
          this.requestOptions,
        )
        const updated = this.normalizeCampaign(response?.data?.data || item)

        this.expiryReviewSuccess = `${item.name} kampanyasının bitiş tarihi ${this.formatDateTime(updated.endDateTime)} olarak 1 gün uzatıldı.`
        await this.loadExpiryReviewData()
      } catch (error) {
        console.error('Kampanya bitiş tarihi uzatılırken hata oluştu:', error)
        this.expiryReviewError = resolveApiErrorMessage(error, 'Kampanya bitiş tarihi uzatılamadı.')
      } finally {
        this.expiryReviewAction = ''
      }
    },
    openExpiryReviewDateDialog(item) {
      this.expiryReviewItem = item
      this.expiryReviewDialogError = ''
      this.expiryReviewNewEndDate = this.formatExpiryReviewDateInput(item.endDateTime)
      this.expiryReviewDateDialog = true
    },
    closeExpiryReviewDateDialog() {
      this.expiryReviewDateDialog = false
      this.expiryReviewItem = {}
      this.expiryReviewNewEndDate = ''
      this.expiryReviewDialogError = ''
    },
    async saveExpiryReviewDate() {
      const item = this.expiryReviewItem
      if (!item?.id || !this.expiryReviewNewEndDate || this.isExpiryReviewItemBusy(item.id)) return
      if (this.expiryReviewNewEndDate < this.todayIstanbulInput) {
        this.expiryReviewDialogError = 'Yeni bitiş tarihi bugünden önce olamaz.'

        return
      }

      this.expiryReviewAction = `${item.id}:date`
      this.expiryReviewDialogError = ''
      this.expiryReviewError = ''
      this.expiryReviewSuccess = ''
      try {
        const response = await axios.put(
          `${process.env.VUE_APP_BASE_API_URL}campaign/expiry-review/${item.id}/end-date`,
          {},
          {
            ...this.requestOptions,
            params: { endDate: this.expiryReviewNewEndDate },
          },
        )
        const updated = this.normalizeCampaign(response?.data?.data || item)

        this.expiryReviewSuccess = `${item.name} kampanyasının bitiş tarihi ${this.formatDateTime(updated.endDateTime)} olarak güncellendi.`
        this.closeExpiryReviewDateDialog()
        await this.loadExpiryReviewData()
      } catch (error) {
        console.error('Kampanya bitiş tarihi güncellenirken hata oluştu:', error)
        this.expiryReviewDialogError = resolveApiErrorMessage(error, 'Kampanya bitiş tarihi güncellenemedi.')
      } finally {
        this.expiryReviewAction = ''
      }
    },
    openExpiryReviewCompleteDialog(item) {
      this.expiryReviewItem = item
      this.expiryReviewDialogError = ''
      this.expiryReviewCompleteDialog = true
    },
    closeExpiryReviewCompleteDialog() {
      this.expiryReviewCompleteDialog = false
      this.expiryReviewItem = {}
      this.expiryReviewDialogError = ''
    },
    async completeExpiryReview() {
      const item = this.expiryReviewItem
      if (!item?.id || this.isExpiryReviewItemBusy(item.id)) return

      this.expiryReviewAction = `${item.id}:complete`
      this.expiryReviewDialogError = ''
      this.expiryReviewError = ''
      this.expiryReviewSuccess = ''
      try {
        await axios.put(
          `${process.env.VUE_APP_BASE_API_URL}campaign/expiry-review/${item.id}/complete`,
          {},
          this.requestOptions,
        )
        this.expiryReviewSuccess = `${item.name} kampanyası tamamlandı ve bağlı aktif linkler kapatıldı.`
        this.closeExpiryReviewCompleteDialog()
        await this.loadExpiryReviewData()
      } catch (error) {
        console.error('Kampanya tamamlanırken hata oluştu:', error)
        this.expiryReviewDialogError = resolveApiErrorMessage(error, 'Kampanya tamamlanamadı.')
      } finally {
        this.expiryReviewAction = ''
      }
    },
    openDeleteDialog(item) {
      this.deleteError = ''
      this.deleteItem = item
      this.deleteDialog = true
    },
    closeDeleteDialog() {
      if (this.deleteLoading) return
      this.deleteDialog = false
      this.deleteItem = {}
    },
    async confirmDelete() {
      if (!this.deleteItem?.id || this.deleteLoading) return
      this.deleteLoading = true
      this.deleteError = ''
      this.tableError = ''
      try {
        await axios.delete(
          `${process.env.VUE_APP_BASE_API_URL}campaign/delete/${this.deleteItem.id}`,
          this.requestOptions,
        )
        const completedAt = new Date().toISOString()
        this.campaigns = this.campaigns
          .map(item => (item.id === this.deleteItem.id
            ? {
              ...item,
              status: 'COMPLETED',
              endDateTime: item.endDateTime || completedAt,
              modified: completedAt,
            }
            : item))
          .filter(item => !(this.selectedStatusFilter === 'ACTIVE' && item.id === this.deleteItem.id))
        this.deleteLoading = false
        this.closeDeleteDialog()
        await this.loadTableData()
      } catch (error) {
        console.error('Kampanya silinirken hata oluştu:', error)
        this.deleteError = resolveApiErrorMessage(error, 'Kampanya tamamlanmışa çekilemedi.')
      } finally {
        this.deleteLoading = false
      }
    },

    // Görsel Havuzu
    openImagePool() {
      this.imageError = ''
      this.imageDialogMode = 'manage'
      this.imageEditorTargetRef = ''
      this.imageDialog = true
      this.$nextTick(() => {
        this.loadImages()
      })
    },
    openEditorImagePool(refName) {
      this.imageError = ''
      this.imageDialogMode = 'editor'
      this.imageEditorTargetRef = refName
      this.imageCarouselTarget = ''
      this.imageDialog = true
      this.$nextTick(() => {
        this.loadImages(true)
      })
    },
    openCarouselImagePool(target) {
      this.imageError = ''
      this.imagePage = 1
      this.imageSearchValue = ''
      this.imageSource = this.candidate && target === 'create' ? 'candidates' : 'uploads'
      this.imageDialogMode = 'carousel'
      this.imageEditorTargetRef = ''
      this.imageCarouselTarget = target
      this.imageDialog = true
      this.$nextTick(() => {
        this.loadImages(true)
      })
    },
    closeImagePool() {
      if (this.imageUploading) return
      this.imageRequestId += 1
      this.imageDialog = false
      this.imageDialogMode = 'manage'
      this.imageEditorTargetRef = ''
      this.imageCarouselTarget = ''
      this.imageForm = { fileName: '' }
      this.imageFile = null
      this.imageFileName = ''
      this.imagePage = 1
      this.images = []
      this.imageError = ''
      if (this.$refs.imageFileInput) this.$refs.imageFileInput.value = ''
    },
    async loadImages(activeOnly = false) {
      this.imageRequestId += 1
      const requestId = this.imageRequestId
      this.imageLoading = true
      this.imageError = ''
      let endpoint = activeOnly ? 'get-all-active' : 'get-all'
      if (this.imageSource === 'candidates') endpoint = 'candidate-media'
      const url = `${process.env.VUE_APP_BASE_API_URL}images/${endpoint}`
      try {
        const response = await axios.post(
          url,
          {
            searchValue: this.imageSearchValue,
            page: this.imagePage - 1,
            size: this.imageItemsPerPage,
            sortBy: this.imageSortBy,
            sortDirection: this.imageSortDirection,
          },
          this.requestOptions,
        )
        if (requestId !== this.imageRequestId) return
        const payload = response?.data?.data
        const list = payload?.list || payload?.content || payload || []
        const total = payload?.totalElements ?? payload?.total ?? list.length
        const totalPages = payload?.totalPages
        this.imageTotalElements = total
        this.imageTotalPagesOverride = totalPages || null
        this.images = list.map(img => {
          const rawSrc = img.localUrl
            || img.url
            || img.imageUrl
            || img.path
            || img.fullPath
            || img.link
            || img.fileUrl
            || img.file
            || img.uri
            || (img.image ? `data:image/*;base64,${img.image}` : '')

          return {
            ...img,
            _rawSrc: rawSrc,
            _nameDraft: img.name || 'İsimsiz Görsel',
            _activeDraft: !!img.active,
            _editing: false,
            _renameLoading: false,
            _src: this.resolveImageSrc(rawSrc),
          }
        })
      } catch (error) {
        if (requestId !== this.imageRequestId) return
        this.images = []
        this.imageTotalElements = 0
        this.imageTotalPagesOverride = null
        this.imageError = resolveApiErrorMessage(error, 'Görseller yüklenemedi. Tekrar deneyin.')
        console.error('Görseller yüklenirken hata oluştu:', error)
      } finally {
        if (requestId === this.imageRequestId) this.imageLoading = false
      }
    },
    onImageSourceChange() {
      this.imagePage = 1
      this.images = []
      this.loadImages(this.imageDialogMode !== 'manage')
    },
    insertImageFromPool(item) {
      // eslint-disable-next-line no-underscore-dangle
      const imageUrl = item?._src || item?.localUrl || item?.url
      if (!imageUrl || !this.imageEditorTargetRef) return

      const editor = this.$refs[this.imageEditorTargetRef]?.quill
      if (!editor) return
      const range = editor.getSelection(true) || { index: editor.getLength(), length: 0 }

      editor.insertEmbed(range.index, 'image', imageUrl, 'user')
      editor.setSelection(range.index + 1, 0)
      this.closeImagePool()
    },
    async selectCarouselImageFromPool(item) {
      if (this.imageUploading) return
      this.imageError = ''
      this.imageUploading = true
      try {
        let registered = item
        if (item.candidateId) {
          const response = await axios.post(
            `${process.env.VUE_APP_BASE_API_URL}images/candidate-media/${encodeURIComponent(item.candidateId)}/select`,
            { url: item.localUrl },
            this.requestOptions,
          )
          registered = response?.data?.data
        }
        const selectedImage = this.extractSelectedImage(registered)
        if (!selectedImage || !selectedImage.url) throw new Error('Görsel seçimi kaydedilemedi.')
        this.applyCarouselImageSelection(selectedImage)
        this.imageUploading = false
        this.closeImagePreview()
        this.closeImagePool()
      } catch (error) {
        this.imageError = resolveApiErrorMessage(error, 'Görsel seçilemedi. Listeyi yenileyip tekrar deneyin.')
        this.closeImagePreview()
      } finally {
        this.imageUploading = false
      }
    },
    clearCarouselImageSelection(target) {
      if (target === 'edit') {
        this.editedCampaign = {
          ...this.editedCampaign,
          carouselImageId: null,
          carouselImageUrl: '',
          carouselImageName: '',
        }

        return
      }

      this.newCampaign = {
        ...this.newCampaign,
        carouselImageId: null,
        carouselImageUrl: '',
        carouselImageName: '',
      }
    },
    applyCarouselImageSelection(selectedImage) {
      if (!selectedImage) return

      if (this.imageCarouselTarget === 'edit') {
        this.editedCampaign = {
          ...this.editedCampaign,
          carouselImageId: selectedImage.id,
          carouselImageUrl: selectedImage.url,
          carouselImageName: selectedImage.name,
        }

        return
      }

      this.newCampaign = {
        ...this.newCampaign,
        carouselImageId: selectedImage.id,
        carouselImageUrl: selectedImage.url,
        carouselImageName: selectedImage.name,
      }
    },
    extractSelectedImage(item) {
      const id = Number(item?.id)
      if (!Number.isSafeInteger(id) || id <= 0) return null

      // eslint-disable-next-line no-underscore-dangle
      const rawUrl = item ? item._src : ''
      // eslint-disable-next-line no-underscore-dangle
      const draftName = item ? item._nameDraft : ''

      return {
        id,
        url: rawUrl || item?.localUrl || item?.url || '',
        name: draftName || item?.name || this.resolveCarouselImageName(item),
      }
    },
    async uploadImage() {
      if (this.imageUploading) return
      this.imageError = ''
      if (!this.imageFile) {
        this.imageError = 'Önce yüklemek istediğiniz görseli seçin.'

        return
      }
      const name = this.imageForm.fileName.trim() || this.imageFile.name
      this.imageUploading = true
      const formData = new FormData()

      formData.append('file', this.imageFile)
      formData.append('folder', 'uploads')
      formData.append('name', name)
      try {
        const uploadResponse = await axios.post(
          `${process.env.VUE_APP_BASE_API_URL2}file/uploadFile`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${getAccessToken()}`,
            },
          },
        )
        const uploaded = uploadResponse?.data?.data || uploadResponse?.data
        const selectedImage = this.extractSelectedImage({ ...uploaded, name })
        if (!selectedImage || !selectedImage.url) throw new Error('Sunucu geçerli bir görsel kaydı döndürmedi.')
        if (this.imageDialogMode === 'carousel') {
          this.applyCarouselImageSelection(selectedImage)
          this.imageUploading = false
          this.closeImagePool()

          return
        }
        this.imageForm = { fileName: '' }
        this.imageFile = null
        this.imageFileName = ''
        if (this.$refs.imageFileInput) this.$refs.imageFileInput.value = ''
        this.imagePage = 1
        this.imageSearchValue = ''
        this.imageSource = 'uploads'
        await this.loadImages(this.imageDialogMode !== 'manage')
      } catch (error) {
        this.imageError = resolveApiErrorMessage(error, 'Görsel yüklenemedi. Dosya biçimini ve boyutunu kontrol edip tekrar deneyin.')
        console.error('Görsel yüklenirken hata oluştu:', error)
      } finally {
        this.imageUploading = false
      }
    },
    /* eslint-disable no-param-reassign, no-underscore-dangle */
    async deleteImage(item) {
      if (!item?.id) return
      item._renameLoading = true
      try {
        await axios.delete(
          `${process.env.VUE_APP_BASE_API_URL}images/${item.id}`,
          this.requestOptions,
        )
        this.loadImages(this.imageDialogMode !== 'manage')
      } catch (error) {
        this.imageError = resolveApiErrorMessage(error, 'Görsel silinemedi.')
        console.error('Görsel silinirken hata oluştu:', error)
      } finally {
        item._renameLoading = false
      }
    },
    /* eslint-enable no-param-reassign, no-underscore-dangle */
    triggerImageSelect() {
      if (this.$refs.imageFileInput) {
        this.$refs.imageFileInput.click()
      }
    },
    onImageFileChange(event) {
      const file = event.target.files?.[0]
      if (!file) return
      this.imageError = ''
      if (!/\.(jpe?g|png|webp)$/i.test(file.name)) {
        this.imageFile = null
        this.imageFileName = ''
        this.imageError = 'JPG, PNG veya WebP biçiminde bir görsel seçin.'
        const input = event.target
        input.value = ''

        return
      }
      this.imageFile = file
      this.imageFileName = file.name
      if (!this.imageForm.fileName.trim()) this.imageForm.fileName = file.name.slice(0, 255)
    },
    onImagePageChange() {
      if (this.imagePage > this.imageTotalPages) {
        this.imagePage = this.imageTotalPages
      }
      this.loadImages(this.imageDialogMode !== 'manage')
    },
    onImageItemsPerPageChange() {
      this.imagePage = 1
      this.loadImages(this.imageDialogMode !== 'manage')
    },
    onImageSearch() {
      this.imagePage = 1
      this.loadImages(this.imageDialogMode !== 'manage')
    },
    resolveImageSrc(src) {
      if (!src) return ''
      try {
        if (src.startsWith('data:')) return src
        if (src.startsWith('http://') || src.startsWith('https://')) return src
        if (src.startsWith('//')) return `${window.location.protocol}${src}`
        if (src.startsWith('/')) {
          const base = process.env.VUE_APP_BASE_URL || window.location.origin

          return `${base}${src}`
        }
        const base = process.env.VUE_APP_BASE_URL
          || process.env.VUE_APP_BASE_API_URL
          || window.location.origin

        return new URL(src, base).href
      } catch (error) {
        return src
      }
    },
    onImageError(item) {
      if (!item) return
      // eslint-disable-next-line no-underscore-dangle
      if (item._rawSrc && item._src !== item._rawSrc) {
        // eslint-disable-next-line no-underscore-dangle, no-param-reassign
        item._src = this.resolveImageSrc(item._rawSrc)

        return
      }
      // eslint-disable-next-line no-param-reassign, no-underscore-dangle
      item._src = notImage
    },
    onLogoSearch() {
      this.loadActiveImages()
    },
    openImagePreview(item) {
      // eslint-disable-next-line no-underscore-dangle
      if (!item?._src) return
      // eslint-disable-next-line no-underscore-dangle
      this.imagePreviewSrc = item._src
      // eslint-disable-next-line no-underscore-dangle
      this.imagePreviewName = item._nameDraft || item.name || 'Görsel Önizleme'
      this.imagePreviewItem = item
      this.imagePreviewDialog = true
    },
    closeImagePreview() {
      this.imagePreviewDialog = false
      this.imagePreviewSrc = ''
      this.imagePreviewName = ''
      this.imagePreviewItem = null
    },
    hasCardImage(html) {
      return !!new DOMParser().parseFromString(html || '', 'text/html').querySelector('img[src]')
    },
    hasCardCrop(html) {
      return !!parseCardCrop(new DOMParser().parseFromString(html || '', 'text/html').querySelector('img')?.getAttribute('data-card-crop'))
    },
    async openCardCropper(refName) {
      const editor = this.$refs[refName]?.quill
      const image = editor?.root.querySelector('img')
      if (!image || this.saveLoading || (refName === 'createDescriptionEditor' && this.composerReadonly)) return
      const src = image.getAttribute('src')
      if (!src) return
      this.cardCropEditor = refName
      this.cardCropSource = src
      this.cardCropInitial = parseCardCrop(image.getAttribute('data-card-crop'))
      this.imagePreviewSrc = src
      this.imagePreviewItem = null
      this.imagePreviewName = 'Kart kapağı'
      await this.openCarouselCropper(true)
    },
    writeCardCrop(refName, value) {
      const editor = this.$refs[refName]?.quill
      const image = editor?.root.querySelector('img')
      if (!image || this.saveLoading || (refName === 'createDescriptionEditor' && this.composerReadonly)) return false
      const blot = Quill.find(image)
      if (!blot) return false
      editor.formatText(editor.getIndex(blot), 1, 'data-card-crop', value, 'user')

      return true
    },
    clearCardCrop(refName) {
      this.writeCardCrop(refName, false)
    },
    applyCardCrop() {
      if (!this.cropReady || this.imageCropLoading) return
      const image = this.$refs[this.cardCropEditor]?.quill?.root.querySelector('img')
      if (!image || image.getAttribute('src') !== this.cardCropSource) {
        this.imageCropError = 'Açıklama görseli değişti. Kırpmayı yeniden açın.'

        return
      }
      const crop = cropFromSelection(this.cropMeta, this.cropRect)
      if (!parseCardCrop(crop) || !this.writeCardCrop(this.cardCropEditor, crop)) return
      this.closeCarouselCropper()
    },
    moveCropWithKeyboard(event) {
      const steps = {
        ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1],
      }
      const step = steps[event.key]
      if (!step || !this.cropReady) return
      event.preventDefault()
      const amount = event.shiftKey ? 10 : 1
      this.cropRect.x = Math.max(this.cropMeta.offsetX, Math.min(this.cropMeta.offsetX + this.cropMeta.renderedWidth - this.cropRect.width, this.cropRect.x + step[0] * amount))
      this.cropRect.y = Math.max(this.cropMeta.offsetY, Math.min(this.cropMeta.offsetY + this.cropMeta.renderedHeight - this.cropRect.height, this.cropRect.y + step[1] * amount))
    },
    async openCarouselCropper(cardMode = false) {
      if (cardMode !== true) {
        this.cardCropEditor = ''
        this.cardCropInitial = null
      }
      if (!this.imagePreviewSrc) return
      this.imageCropDialog = true
      this.imageCropSrc = ''
      this.imageCropObjectUrl = ''
      this.imageCropName = this.imagePreviewName || 'carousel-image'
      this.imageCropItem = this.imagePreviewItem
      this.imageCropScale = this.cardCropEditor ? 100 / (this.cardCropInitial?.zoom || 1) : 72
      this.imageCropLoading = true
      this.imageCropLoaded = false
      this.imageCropError = ''
      this.imageCropPreviewSrc = ''
      this.cropMeta = {
        naturalWidth: 0,
        naturalHeight: 0,
        offsetX: 0,
        offsetY: 0,
        renderedWidth: 0,
        renderedHeight: 0,
      }
      this.cropRect = {
        x: 0,
        y: 0,
        width: 0,
        height: 0,
      }
      this.detachCropDragListeners()
      window.removeEventListener('resize', this.handleCropViewportResize)
      window.addEventListener('resize', this.handleCropViewportResize)
      try {
        // Card cropping stores geometry only; no canvas export or credentialed proxy is needed.
        this.imageCropSrc = this.cardCropEditor ? this.cardCropSource : await this.resolveCropImageSource()
      } catch (error) {
        console.error('Karosel crop görseli hazırlanamadı:', error)
        this.imageCropError = resolveApiErrorMessage(error, 'Görsel kırpma aracı için yüklenemedi.')
        this.imageCropLoading = false
      }
    },
    closeCarouselCropper() {
      this.cardCropEditor = ''
      this.cardCropSource = ''
      this.cardCropInitial = null
      this.imageCropDialog = false
      this.clearCropMeasurementSchedule()
      this.imageCropSrc = ''
      if (this.imageCropObjectUrl) {
        URL.revokeObjectURL(this.imageCropObjectUrl)
      }
      this.imageCropObjectUrl = ''
      this.imageCropName = ''
      this.imageCropItem = null
      this.imageCropScale = 72
      this.imageCropLoading = false
      this.imageCropLoaded = false
      this.imageCropError = ''
      this.imageCropPreviewSrc = ''
      this.cropMeta = {
        naturalWidth: 0,
        naturalHeight: 0,
        offsetX: 0,
        offsetY: 0,
        renderedWidth: 0,
        renderedHeight: 0,
      }
      this.cropRect = {
        x: 0,
        y: 0,
        width: 0,
        height: 0,
      }
      this.detachCropDragListeners()
      window.removeEventListener('resize', this.handleCropViewportResize)
    },
    onCropImageLoad(event) {
      if (!this.imageCropDialog || (event?.target && event.target !== this.$refs.imageCropImage)) return
      const image = this.$refs.imageCropImage
      if (!image?.naturalWidth || !image?.naturalHeight) {
        this.onCropImageError(event)

        return
      }
      this.imageCropLoaded = true
      this.imageCropLoading = false
      this.imageCropError = ''
      this.scheduleCropMeasurement(true)
    },
    onCropImageError(event) {
      if (!this.imageCropDialog || (event?.target && event.target !== this.$refs.imageCropImage)) return
      this.imageCropLoaded = false
      this.imageCropLoading = false
      this.clearCropMeasurementSchedule()
      this.imageCropError = 'Görsel yüklenemedi. Görsel bağlantısını kontrol edip kırpmayı yeniden açın.'
    },
    handleCropViewportResize() {
      this.scheduleCropMeasurement(false)
    },
    clearCropMeasurementSchedule() {
      if (this.cropMeasureTimer) {
        clearTimeout(this.cropMeasureTimer)
        this.cropMeasureTimer = null
      }
      if (this.cropMeasureFrame) {
        cancelAnimationFrame(this.cropMeasureFrame)
        this.cropMeasureFrame = null
      }
    },
    scheduleCropMeasurement(resetSelection = false) {
      this.clearCropMeasurementSchedule()
      this.cropMeasureTimer = setTimeout(() => {
        this.cropMeasureFrame = requestAnimationFrame(() => {
          this.measureCropImage(resetSelection)
        })
      }, 120)
    },
    measureCropImage(resetSelection = false) {
      const shell = this.$refs.imageCropShell
      const image = this.$refs.imageCropImage
      if (!this.imageCropLoaded || !shell || !image || !image.complete || !image.naturalWidth || !image.naturalHeight) return

      // Dialog enter animations transform viewport bounds, not CSS coordinates.
      const boxWidth = image.clientWidth
      const boxHeight = image.clientHeight
      if (!shell.clientWidth || !boxWidth || !boxHeight) return

      const { naturalWidth, naturalHeight } = image
      const imageRatio = naturalWidth / naturalHeight
      const boxRatio = boxWidth / boxHeight

      let renderedWidth = boxWidth
      let renderedHeight = boxHeight
      let innerOffsetX = 0
      let innerOffsetY = 0

      // The <img> element stretches to the shell, but object-fit: contain
      // may leave letterboxing inside the element. Crop math must use the
      // real painted image box, not the outer element rectangle.
      if (boxRatio > imageRatio) {
        renderedWidth = boxHeight * imageRatio
        renderedHeight = boxHeight
        innerOffsetX = (boxWidth - renderedWidth) / 2
      } else {
        renderedWidth = boxWidth
        renderedHeight = boxWidth / imageRatio
        innerOffsetY = (boxHeight - renderedHeight) / 2
      }

      const nextCropMeta = {
        naturalWidth,
        naturalHeight,
        offsetX: image.offsetLeft + innerOffsetX,
        offsetY: image.offsetTop + innerOffsetY,
        renderedWidth,
        renderedHeight,
      }
      const previousCropMeta = { ...this.cropMeta }
      const previousCropRect = { ...this.cropRect }

      this.cropMeta = nextCropMeta

      if (resetSelection || !previousCropMeta.renderedWidth || !previousCropMeta.renderedHeight || !previousCropRect.width || !previousCropRect.height) {
        this.resetCropRect()
        if (this.cardCropInitial) {
          this.cropRect.x = this.cropMeta.offsetX + (this.cropMeta.renderedWidth - this.cropRect.width) * (this.cardCropInitial.x / 100)
          this.cropRect.y = this.cropMeta.offsetY + (this.cropMeta.renderedHeight - this.cropRect.height) * (this.cardCropInitial.y / 100)
          this.cardCropInitial = null
        }

        return
      }

      const relativeX = (previousCropRect.x - previousCropMeta.offsetX) / previousCropMeta.renderedWidth
      const relativeY = (previousCropRect.y - previousCropMeta.offsetY) / previousCropMeta.renderedHeight
      const relativeWidth = previousCropRect.width / previousCropMeta.renderedWidth
      const relativeHeight = previousCropRect.height / previousCropMeta.renderedHeight

      const width = nextCropMeta.renderedWidth * relativeWidth
      const height = nextCropMeta.renderedHeight * relativeHeight
      const minX = nextCropMeta.offsetX
      const minY = nextCropMeta.offsetY
      const maxX = nextCropMeta.offsetX + nextCropMeta.renderedWidth - width
      const maxY = nextCropMeta.offsetY + nextCropMeta.renderedHeight - height

      this.cropRect = {
        width,
        height,
        x: Math.min(Math.max(nextCropMeta.offsetX + (nextCropMeta.renderedWidth * relativeX), minX), maxX),
        y: Math.min(Math.max(nextCropMeta.offsetY + (nextCropMeta.renderedHeight * relativeY), minY), maxY),
      }
      this.refreshCropPreview()
    },
    async resolveCropImageSource() {
      /* eslint-disable no-underscore-dangle, dot-notation */
      const sourceUrl = this.imageCropItem?.['_rawSrc']
        || this.imageCropItem?.['_src']
        || this.imagePreviewSrc
      /* eslint-enable no-underscore-dangle, dot-notation */

      if (!sourceUrl) {
        throw new Error('Kırpılacak görsel bulunamadı.')
      }

      if (sourceUrl.startsWith('data:') || sourceUrl.startsWith('blob:')) {
        return sourceUrl
      }

      try {
        const source = new URL(sourceUrl, window.location.origin)
        if (source.origin === window.location.origin) {
          return source.href
        }
      } catch (error) {
        return sourceUrl
      }

      const response = await axios.get(
        `${process.env.VUE_APP_BASE_API_URL}images/proxy`,
        {
          ...this.requestOptions,
          params: {
            url: sourceUrl,
          },
          responseType: 'blob',
        },
      )

      const objectUrl = URL.createObjectURL(response.data)
      this.imageCropObjectUrl = objectUrl

      return objectUrl
    },
    resetCropRect() {
      if (!this.imageCropLoaded || !this.cropMeta.renderedWidth || !this.cropMeta.renderedHeight) return

      const baseWidth = Math.min(
        this.cropMeta.renderedWidth,
        this.cropMeta.renderedHeight * this.carouselCropAspectRatio,
      )
      const width = baseWidth * (this.imageCropScale / 100)
      const height = width / this.carouselCropAspectRatio

      this.cropRect = {
        width,
        height,
        x: this.cropMeta.offsetX + ((this.cropMeta.renderedWidth - width) / 2),
        y: this.cropMeta.offsetY + ((this.cropMeta.renderedHeight - height) / 2),
      }
      this.refreshCropPreview()
    },
    onCropScaleInput() {
      if (!this.cropReady) {
        this.resetCropRect()

        return
      }

      const centerX = this.cropRect.x + (this.cropRect.width / 2)
      const centerY = this.cropRect.y + (this.cropRect.height / 2)
      const baseWidth = Math.min(
        this.cropMeta.renderedWidth,
        this.cropMeta.renderedHeight * this.carouselCropAspectRatio,
      )
      const width = baseWidth * (this.imageCropScale / 100)
      const height = width / this.carouselCropAspectRatio
      const minX = this.cropMeta.offsetX
      const minY = this.cropMeta.offsetY
      const maxX = this.cropMeta.offsetX + this.cropMeta.renderedWidth - width
      const maxY = this.cropMeta.offsetY + this.cropMeta.renderedHeight - height

      this.cropRect = {
        width,
        height,
        x: Math.min(Math.max(centerX - (width / 2), minX), maxX),
        y: Math.min(Math.max(centerY - (height / 2), minY), maxY),
      }
      this.refreshCropPreview()
    },
    startCropDrag(event) {
      if (!this.cropReady) return
      const pointer = this.getCropPointer(event)
      this.cropDragState = {
        pointerX: pointer.x,
        pointerY: pointer.y,
        originX: this.cropRect.x,
        originY: this.cropRect.y,
      }
      this.attachCropDragListeners()
    },
    onCropDragMove(event) {
      if (!this.cropDragState) return
      const pointer = this.getCropPointer(event)
      const deltaX = pointer.x - this.cropDragState.pointerX
      const deltaY = pointer.y - this.cropDragState.pointerY
      const minX = this.cropMeta.offsetX
      const minY = this.cropMeta.offsetY
      const maxX = this.cropMeta.offsetX + this.cropMeta.renderedWidth - this.cropRect.width
      const maxY = this.cropMeta.offsetY + this.cropMeta.renderedHeight - this.cropRect.height

      this.cropRect = {
        ...this.cropRect,
        x: Math.min(Math.max(this.cropDragState.originX + deltaX, minX), maxX),
        y: Math.min(Math.max(this.cropDragState.originY + deltaY, minY), maxY),
      }
      this.refreshCropPreview()
    },
    stopCropDrag() {
      this.cropDragState = null
      this.detachCropDragListeners()
    },
    attachCropDragListeners() {
      window.addEventListener('mousemove', this.onCropDragMove)
      window.addEventListener('mouseup', this.stopCropDrag)
      window.addEventListener('touchmove', this.onCropDragMove, { passive: false })
      window.addEventListener('touchend', this.stopCropDrag)
    },
    detachCropDragListeners() {
      window.removeEventListener('mousemove', this.onCropDragMove)
      window.removeEventListener('mouseup', this.stopCropDrag)
      window.removeEventListener('touchmove', this.onCropDragMove)
      window.removeEventListener('touchend', this.stopCropDrag)
    },
    getCropPointer(event) {
      if (event.touches && event.touches[0]) {
        return {
          x: event.touches[0].clientX,
          y: event.touches[0].clientY,
        }
      }

      if (event.changedTouches && event.changedTouches[0]) {
        return {
          x: event.changedTouches[0].clientX,
          y: event.changedTouches[0].clientY,
        }
      }

      return {
        x: event.clientX,
        y: event.clientY,
      }
    },
    buildCropCanvas(outputWidth = 1400) {
      if (!this.cropReady) return null
      const image = this.$refs.imageCropImage
      if (!image) return null

      const canvas = document.createElement('canvas')
      const outputHeight = Math.round(outputWidth / this.carouselCropAspectRatio)
      const sx = ((this.cropRect.x - this.cropMeta.offsetX) / this.cropMeta.renderedWidth) * this.cropMeta.naturalWidth
      const sy = ((this.cropRect.y - this.cropMeta.offsetY) / this.cropMeta.renderedHeight) * this.cropMeta.naturalHeight
      const sw = (this.cropRect.width / this.cropMeta.renderedWidth) * this.cropMeta.naturalWidth
      const sh = (this.cropRect.height / this.cropMeta.renderedHeight) * this.cropMeta.naturalHeight

      canvas.width = outputWidth
      canvas.height = outputHeight
      const ctx = canvas.getContext('2d')
      ctx.drawImage(image, sx, sy, sw, sh, 0, 0, outputWidth, outputHeight)

      return canvas
    },
    refreshCropPreview() {
      if (this.imageCropLoaded) this.imageCropError = ''
    },
    async uploadCroppedCarouselImage() {
      if (!this.cropReady) return
      this.imageCropLoading = true
      this.imageCropError = ''
      try {
        const canvas = this.buildCropCanvas()
        if (!canvas) {
          throw new Error('Kırpma önizlemesi hazırlanamadı.')
        }

        const blob = await new Promise((resolve, reject) => {
          canvas.toBlob(result => {
            if (result) {
              resolve(result)
            } else {
              reject(new Error('Kırpılmış görsel üretilemedi.'))
            }
          }, 'image/jpeg', 0.92)
        })

        const formData = new FormData()
        const baseName = this.sanitizeImageName(this.imageCropName || 'carousel-image')
        const fileName = `${baseName}-carousel-${Date.now()}.jpg`
        const file = new File([blob], fileName, { type: 'image/jpeg' })

        formData.append('file', file)
        formData.append('folder', 'uploads')

        const uploadResponse = await axios.post(
          `${process.env.VUE_APP_BASE_API_URL2}file/uploadFile`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${getAccessToken()}`,
              'Content-Type': 'multipart/form-data',
            },
          },
        )

        const uploadedImageId = uploadResponse?.data?.id || uploadResponse?.data?.data?.id
        const uploadedLocalUrl = uploadResponse?.data?.localUrl || uploadResponse?.data?.data?.localUrl || ''
        if (uploadedImageId) {
          const renameResponse = await axios.put(
            `${process.env.VUE_APP_BASE_API_URL}images/change-name/${uploadedImageId}?name=${encodeURIComponent(fileName)}`,
            {},
            this.requestOptions,
          )
          if (this.imageDialogMode === 'carousel') {
            this.applyCarouselImageSelection(this.extractSelectedImage(renameResponse?.data?.data || {
              id: uploadedImageId,
              localUrl: uploadedLocalUrl,
              name: fileName,
            }))
          }
        }

        await this.loadImages(this.imageDialogMode !== 'manage')
        this.closeCarouselCropper()
        this.closeImagePreview()
        if (this.imageDialogMode === 'carousel') {
          this.closeImagePool()
        }
      } catch (error) {
        console.error('Karosel görseli kırpılırken hata oluştu:', error)
        this.imageCropError = resolveApiErrorMessage(error, 'Karosel görseli kırpılamadı.')
      } finally {
        this.imageCropLoading = false
      }
    },
    sanitizeImageName(value) {
      return String(value || 'carousel-image')
        .replace(/\.[^/.]+$/, '')
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9-_]+/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '') || 'carousel-image'
    },
    formatDateTimeLocal(val) {
      if (!val) return null
      const parts = this.parseCampaignDateParts(val)

      if (!parts) return val

      return `${parts.year}-${this.padDatePart(parts.month)}-${this.padDatePart(parts.day)}T${this.padDatePart(parts.hour)}:${this.padDatePart(parts.minute)}:${this.padDatePart(parts.second)}`
    },
    toBackendDateTime(value) {
      if (!value) return null

      return this.formatDateTimeLocal(value)
    },
    padDatePart(value) {
      return String(value).padStart(2, '0')
    },
    getIstanbulDateParts(date) {
      if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
        return null
      }
      const parts = new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Europe/Istanbul',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
      }).formatToParts(date).reduce((acc, part) => {
        acc[part.type] = part.value

        return acc
      }, {})

      return {
        year: Number(parts.year),
        month: Number(parts.month),
        day: Number(parts.day),
        hour: Number(parts.hour === '24' ? '00' : parts.hour),
        minute: Number(parts.minute),
        second: Number(parts.second),
      }
    },
    parseCampaignDateParts(value) {
      if (!value) return null
      if (typeof value === 'number') {
        if (!Number.isFinite(value)) return null

        return this.getIstanbulDateParts(new Date(value))
      }
      const normalized = String(value).trim()

      const trDateMatch = normalized.match(/^(\d{2})[./](\d{2})[./](\d{4})(?:\s+(\d{2}):(\d{2})(?::(\d{2}))?)?$/)
      if (trDateMatch) {
        const [, day, month, year, hour = '00', minute = '00', second = '00'] = trDateMatch

        return {
          year: Number(year),
          month: Number(month),
          day: Number(day),
          hour: Number(hour),
          minute: Number(minute),
          second: Number(second),
        }
      }

      const localDateTimeMatch = normalized.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T\s](\d{2}):(\d{2})(?::(\d{2}))?)?$/)
      if (localDateTimeMatch) {
        const [, year, month, day, hour = '00', minute = '00', second = '00'] = localDateTimeMatch

        return {
          year: Number(year),
          month: Number(month),
          day: Number(day),
          hour: Number(hour),
          minute: Number(minute),
          second: Number(second),
        }
      }

      const parsed = new Date(normalized)

      return Number.isNaN(parsed.getTime()) ? null : this.getIstanbulDateParts(parsed)
    },
    campaignDatePartsComparable(parts, includeTime = true) {
      if (!parts) return null

      return Number(`${parts.year}${this.padDatePart(parts.month)}${this.padDatePart(parts.day)}${includeTime ? `${this.padDatePart(parts.hour)}${this.padDatePart(parts.minute)}${this.padDatePart(parts.second)}` : ''}`)
    },
    compareCampaignDateTimeValues(left, right) {
      const leftParts = this.parseCampaignDateParts(left)
      const rightParts = this.parseCampaignDateParts(right)

      if (!leftParts || !rightParts) return 1

      return this.campaignDatePartsComparable(leftParts) - this.campaignDatePartsComparable(rightParts)
    },
    isPastCampaignEndDate(value) {
      const parts = this.parseCampaignDateParts(value)
      if (!parts) return false
      const todayParts = this.getIstanbulDateParts(new Date())

      return this.campaignDatePartsComparable(parts, false) < this.campaignDatePartsComparable(todayParts, false)
    },
    pastCampaignEndDateActivationMessage() {
      return 'Bitiş tarihi geçmişte olan kampanya aktife alınamaz. Lütfen bitiş tarihini güncelleyin.'
    },
    toBackendCampaignEndDate(value) {
      if (!value) return null
      const parts = this.parseCampaignDateParts(value)
      if (!parts) return value

      return `${this.padDatePart(parts.day)}/${this.padDatePart(parts.month)}/${parts.year} ${this.padDatePart(parts.hour)}:${this.padDatePart(parts.minute)}:${this.padDatePart(parts.second)}`
    },
    buildCampaignUpdatePayload(source) {
      const categoryIds = this.resolveCategoryIds(source)

      return {
        id: source.id,
        name: source.name,
        description: source.description || '',
        offer: source.mainCampaignId ? { id: Number(source.mainCampaignId) } : null,
        carouselImage: source.carouselImageId ? { id: Number(source.carouselImageId) } : null,
        realLink: source.realLink || source.trackingLink || '',
        premiumOnly: !!source.premiumOnly,
        carouselCampaign: !!source.carouselCampaign,
        proposal: !!(source.proposal ?? source.recommended),
        campaignKind: source.campaignKind || source.type || 'CAMPAIGN',
        shortDescription: source.shortDescription || '',
        categories: categoryIds.map(id => ({ id })),
        activationDate: this.toBackendDateTime(source.startDateTime || source.activationDate),
        endDate: this.toBackendCampaignEndDate(source.endDateTime || source.endDate),
        status: source.status || 'ACTIVE',
        promoteAsNew: source.status === 'ACTIVE' && !!source.promoteAsNew,
        cookieDurationInMinutes: source.cookieDurationInMinutes || 0,
      }
    },
    resolveCampaignSortField(sortBy) {
      const map = {
        mainCampaignOfferId: 'name',
        mainCampaignSourceName: 'name',
        mainCampaignPayout: 'created',
        payout: 'created',
        startDateTime: 'activationDate',
        endDateTime: 'endDate',
        recommended: 'proposal',
      }

      return map[sortBy] || sortBy || 'created'
    },
  },
}
</script>

<style scoped>
.campaign-create-card { background: #fff !important; color: #252629; border-radius: 8px; display: flex; flex-direction: column; max-height: 91vh; font-family: Inter, Arial, sans-serif; letter-spacing: 0; }
.campaign-create-header { padding: 20px 24px; border-bottom: 1px solid #e3e7eb; display: flex; align-items: center; justify-content: space-between; gap: 16px; flex-shrink: 0; }
.campaign-create-header h2 { font-size: 17px; line-height: 1.4; margin: 0; overflow-wrap: anywhere; color: #252629; }
.campaign-create-header p { font-size: 12px; color: #697586; margin: 5px 0 0; }
.campaign-create-body { overflow-y: auto; min-height: 0; padding: 24px; }
.campaign-create-grid { display: grid; grid-template-columns: minmax(0, .85fr) minmax(0, 1.35fr); gap: 28px; align-items: start; }
.campaign-create-fields, .campaign-create-description { min-width: 0; border: 0; padding: 0; margin: 0; }
.composer-label { display: block; font-size: 12px; font-weight: 600; margin: 17px 0 7px; color: #252629; }
.campaign-create-fields > .composer-label:first-child, .campaign-create-description > .composer-label:first-child { margin-top: 0; }
.composer-input { display: block; width: 100%; min-width: 0; border: 1px solid #d7dce2; background: #fff; color: #252629; border-radius: 5px; padding: 10px 12px; font-size: 14px; line-height: 1.5; }
.composer-character-count { margin-top: 5px; text-align: right; color: #697586; font-size: 12px; font-variant-numeric: tabular-nums; }
select.composer-input { appearance: auto; }
.composer-input[readonly] { background: #f5f7f9; color: #697586; }
textarea.composer-input { min-height: 78px; resize: vertical; line-height: 1.65; }
.composer-input:focus-visible, .composer-button:focus-visible, .campaign-create-card summary:focus-visible { outline: 2px solid #4378b5; outline-offset: 3px; }
.composer-offer ::v-deep .v-input__slot { min-height: 43px !important; border-radius: 5px; }
.composer-offer ::v-deep fieldset { border-color: #d7dce2; }
.composer-offer ::v-deep input { font-size: 14px; color: #252629 !important; }
.composer-mode-picker { display: inline-flex; border: 1px solid #d7dce2; border-radius: 5px; overflow: hidden; max-width: 100%; }
.composer-mode-picker label { position: relative; cursor: pointer; margin: 0; }
.composer-mode-picker input { position: absolute; opacity: 0; width: 100%; height: 100%; }
.composer-mode-picker span { display: block; padding: 9px 17px; font-size: 13px; border-right: 1px solid #d7dce2; }
.composer-mode-picker label:last-child span { border-right: 0; }
.composer-mode-picker input:checked + span { background: #e9f1f9; color: #1e5a8a; font-weight: 600; }
.composer-mode-picker input:focus-visible + span { outline: 2px solid #4378b5; outline-offset: -3px; }
.composer-categories { border: 1px solid #d7dce2; border-radius: 5px; font-size: 13px; }
.composer-categories summary { cursor: pointer; padding: 11px 12px; overflow-wrap: anywhere; }
.composer-category-options { border-top: 1px solid #e3e7eb; padding: 12px; display: grid; gap: 10px; max-height: 220px; overflow-y: auto; }
.composer-toggles { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px 18px; margin-top: 20px; }
.composer-checkbox { display: flex; gap: 8px; align-items: flex-start; font-size: 13px; line-height: 1.5; cursor: pointer; margin: 0; }
.composer-checkbox input { flex: 0 0 16px; width: 16px; height: 16px; margin-top: 2px; accent-color: #c72b38; }
.composer-checkbox span { min-width: 0; overflow-wrap: anywhere; }
.composer-carousel img { display: block; max-width: 100%; max-height: 160px; margin: 10px 0; }
.composer-activation { color: #697586; font-size: 12px; margin: 18px 0; }
.composer-editor-heading { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin: 17px 0 7px; flex-wrap: wrap; }
.composer-editor-heading .composer-label { margin: 0; }
.composer-inline-actions { display: flex; gap: 8px; flex-wrap: wrap; }
.composer-button { display: inline-flex; align-items: center; justify-content: center; gap: 5px; border: 1px solid #d9dce1; border-radius: 5px; padding: 9px 12px; background: #fff; color: #252629; font-size: 14px; line-height: 1.5; letter-spacing: 0; cursor: pointer; min-height: 40px; }
.composer-button:hover:not(:disabled) { background: #f5f7f9; }
.composer-inline-actions .composer-button { padding: 6px 9px; min-height: 32px; font-size: 12px; }
.composer-button--primary { background: #c72b38; border-color: #c72b38; color: #fff; }
.composer-button--primary:hover:not(:disabled) { background: #ac2330; }
.composer-button--danger { color: #b32635; }
.composer-button:disabled { opacity: .5; cursor: not-allowed; }
.campaign-create-description .campaign-description-editor { background: #fff; border: 0; border-radius: 5px; }
.campaign-create-description ::v-deep .ql-tooltip { max-width: 100%; white-space: normal; left: 0 !important; }
.campaign-create-description ::v-deep .ql-toolbar { background: #fff; border-color: #d7dce2; border-radius: 5px 5px 0 0; padding: 8px; }
.campaign-create-description ::v-deep .ql-toolbar .ql-formats { margin-right: 8px; }
.campaign-create-description ::v-deep .ql-container { background: #fff; color: #252629; border-color: #d7dce2; border-radius: 0 0 5px 5px; font: 13px/1.75 Arial, sans-serif; }
.campaign-create-description ::v-deep .ql-editor { padding: 16px; min-height: 540px; max-height: none; line-height: 1.75; overflow-wrap: anywhere; }
.campaign-create-description ::v-deep .ql-editor img { display: block; max-width: 100%; height: auto; margin-bottom: 16px; }
.campaign-create-description ::v-deep .ql-editor h1,
.campaign-create-description ::v-deep .ql-editor h2,
.campaign-create-description ::v-deep .ql-editor h3,
.campaign-create-description ::v-deep .ql-editor h4,
.campaign-create-description ::v-deep .ql-editor h5,
.campaign-create-description ::v-deep .ql-editor h6 { line-height: 1.5; margin: 12px 0; }
.campaign-create-description ::v-deep .ql-editor h1 { font-size: 28px; }
.campaign-create-description ::v-deep .ql-editor h2 { font-size: 24px; }
.campaign-create-description ::v-deep .ql-editor h3 { font-size: 20px; }
.campaign-create-description ::v-deep .ql-editor h4 { font-size: 18px; }
.campaign-create-description ::v-deep .ql-editor h5 { font-size: 16px; }
.campaign-create-description ::v-deep .ql-editor h6 { font-size: 14px; }
.campaign-create-description ::v-deep .ql-editor p { margin: 0 0 12px; }
.composer-upload-status { font-size: 12px; color: #1e5a8a; }
.candidate-evidence { border-top: 1px solid #e3e7eb; padding-top: 16px; font-size: 12px; }
.candidate-evidence details { margin-top: 16px; }
.candidate-comparison { width: 100%; margin-top: 16px; border-collapse: collapse; table-layout: fixed; }
.candidate-comparison th, .candidate-comparison td { padding: 8px 4px; border-bottom: 1px solid #dce0e5; text-align: left; vertical-align: top; overflow-wrap: anywhere; }
.candidate-evidence dd { margin: 4px 0 12px; overflow-wrap: anywhere; }
.candidate-evidence summary { cursor: pointer; font-weight: 600; }
.candidate-evidence pre { white-space: pre-wrap; overflow-wrap: anywhere; max-height: 220px; overflow: auto; font-size: 12px; margin-top: 10px; }
.composer-source-review { margin-top: 16px; }
.composer-warning { padding: 12px; background: #fff8e5; border: 1px solid #ecd7a2; border-radius: 5px; margin-top: 16px; line-height: 1.6; }
.campaign-create-footer { display: flex; align-items: center; gap: 10px; padding: 18px 24px; border-top: 1px solid #e3e7eb; flex-shrink: 0; flex-wrap: wrap; }
.composer-footnote { margin-right: auto; color: #697586; font-size: 12px; }
@media (max-width: 760px) {
  .campaign-create-grid { grid-template-columns: minmax(0, 1fr); gap: 20px; }
  .campaign-create-body { padding: 16px; }
  .campaign-create-header { padding: 14px 16px; }
  .campaign-create-header h2 { font-size: 15px; }
  .campaign-create-footer { padding: 14px 16px; }
  .campaign-create-footer .composer-button { flex: 1 1 auto; }
  .composer-footnote { flex-basis: 100%; }
  .campaign-create-description ::v-deep .ql-editor { min-height: 350px; padding: 12px; }
}
.campaign-view-tabs {
  border-bottom: 1px solid rgba(148, 163, 184, 0.2);
}

.expiry-review-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  padding: 18px 24px;
  border-bottom: 1px solid rgba(148, 163, 184, 0.2);
}

.expiry-review-toolbar__copy {
  display: grid;
  gap: 4px;
  min-width: 0;
}

.expiry-review-toolbar__copy span {
  color: rgba(100, 116, 139, 0.92);
  font-size: 13px;
}

.expiry-review-toolbar__actions,
.expiry-review-row-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.expiry-review-search {
  width: 320px;
}

.expiry-review-table {
  width: 100%;
}

.expiry-review-table :deep(.v-data-table__wrapper) {
  overflow-x: auto;
}

.expiry-review-table :deep(table) {
  min-width: 1180px;
}

.expiry-review-table :deep(th),
.expiry-review-table :deep(td) {
  white-space: nowrap;
}

.campaign-toolbar {
  align-items: flex-end;
  row-gap: 12px;
}

.campaign-toolbar__filters,
.campaign-toolbar__actions {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.campaign-toolbar__filters {
  flex: 1 1 820px;
  min-width: 0;
}

.campaign-toolbar__actions {
  flex: 0 0 auto;
  margin-left: auto;
  justify-content: flex-end;
  padding-bottom: 2px;
}

.search-field {
  flex: 1 1 420px;
  min-width: 340px;
  max-width: none;
}

.status-filter {
  flex: 0 0 190px;
  min-width: 180px;
  max-width: 220px;
}
.campaign-editor-toolbar {
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
  flex-wrap: wrap;
}
.selected-carousel-image {
  display: inline-block;
}
.selected-carousel-image__preview {
  border: 1px solid rgba(148, 163, 184, 0.26);
  border-radius: 12px;
  overflow: hidden;
  background: rgba(15, 23, 42, 0.08);
}
.create-offer-meta {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  padding: 14px 16px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.04);
}
.create-offer-meta__label {
  display: block;
  margin-bottom: 4px;
  font-size: 12px;
  opacity: 0.72;
}
.create-toggle-note {
  opacity: 0.72;
  margin-top: -8px;
}
.campaign-description-editor {
  background: rgba(15, 23, 42, 0.55);
  border: 1px solid rgba(148, 163, 184, 0.26);
  border-radius: 16px;
  overflow: hidden;
}
.campaign-description-editor :deep(.ql-toolbar.ql-snow) {
  border: 0;
  border-bottom: 1px solid rgba(148, 163, 184, 0.22);
  background: rgba(15, 23, 42, 0.92);
}
.campaign-description-editor :deep(.ql-container.ql-snow) {
  border: 0;
  min-height: 240px;
  font-size: 14px;
}
.campaign-description-editor :deep(.ql-editor) {
  min-height: 240px;
  color: #f8fafc;
}
.campaign-description-editor :deep(.ql-editor.ql-blank::before) {
  color: rgba(226, 232, 240, 0.46);
}
.campaign-table {
  width: 100%;
}
.campaign-table :deep(.v-data-table__wrapper) {
  overflow-x: auto;
}
.campaign-table :deep(table) {
  width: max-content;
  min-width: 1560px;
}
.campaign-table :deep(th),
.campaign-table :deep(td) {
  white-space: nowrap;
}
.campaign-table :deep(thead th:nth-child(15)),
.campaign-table :deep(tbody td:nth-child(15)) {
  width: 240px !important;
  min-width: 240px !important;
  max-width: 240px !important;
  vertical-align: top;
}
.campaign-datetime-empty :deep(input::-webkit-datetime-edit) {
  color: transparent;
}
.campaign-datetime-empty :deep(input::-webkit-datetime-edit-fields-wrapper) {
  color: transparent;
}
.campaign-datetime-empty :deep(input::-webkit-calendar-picker-indicator) {
  opacity: 0.7;
}
.campaign-categories-cell {
  display: block !important;
  width: 240px !important;
  max-width: 240px !important;
  white-space: normal !important;
  overflow: hidden !important;
  line-height: 1.45;
  word-break: break-word;
  overflow-wrap: anywhere;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  max-height: calc(1.45em * 2);
}
.image-dialog-card {
  margin-top: 12px;
}
.image-card {
  height: 100%;
}
.image-card__img {
  background: #f8f8f8;
  border-bottom: 1px solid #f1f1f1;
}
.image-card__name {
  font-weight: 600;
  font-size: 13px;
  max-width: 170px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}
.image-col {
  display: flex;
}
.image-col > .v-card {
  width: 100%;
}
@media (min-width: 1264px) {
  .image-col {
    flex: 0 0 20%;
    max-width: 20%;
  }
}
.new-image-card {
  border-style: dashed;
  border-width: 1px;
  border-color: #c7c7c7;
  background: #fafafa;
}
.new-image-row {
  margin-left: 0;
  margin-right: 0;
}
.image-input-control .v-input__control {
  min-height: 40px;
}
.image-upload-btn {
  height: 40px;
  min-width: 170px;
}
.image-submit-btn {
  height: 40px;
  min-width: 110px;
}
.image-file-name {
  max-width: 180px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.carousel-crop-shell {
  position: relative;
  width: 100%;
  height: clamp(180px, 42vh, 420px);
  border-radius: 14px;
  overflow: hidden;
  background: repeating-linear-gradient(
    45deg,
    #f3f4f6,
    #f3f4f6 14px,
    #ebeef2 14px,
    #ebeef2 28px
  );
}
.carousel-crop-image {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
  user-select: none;
  -webkit-user-drag: none;
}
.carousel-crop-loading {
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
}
.carousel-crop-controls {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 160px;
  align-items: center;
  gap: 20px;
  margin-top: 16px;
}
.carousel-crop-selection {
  position: absolute;
  border: 2px solid rgba(227, 25, 55, 0.95);
  border-radius: 14px;
  box-shadow: 0 0 0 9999px rgba(15, 23, 42, 0.42);
  cursor: move;
  touch-action: none;
}
.carousel-crop-selection__label {
  position: absolute;
  top: -34px;
  left: 0;
  padding: 4px 10px;
  border-radius: 999px;
  background: rgba(227, 25, 55, 0.96);
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.02em;
}
.carousel-crop-preview {
  overflow: hidden;
  border: 1px solid rgba(148, 163, 184, 0.35);
  border-radius: 16px;
  background: #0f172a;
}
.carousel-crop-preview__viewport {
  position: relative;
  width: 100%;
  aspect-ratio: 35 / 18;
  overflow: hidden;
}
.carousel-crop-preview__img {
  position: absolute;
  max-width: none;
  user-select: none;
  -webkit-user-drag: none;
}
@media (max-width: 960px) {
  .expiry-review-toolbar {
    align-items: stretch;
    flex-direction: column;
  }
  .expiry-review-toolbar__actions {
    align-items: stretch;
    flex-direction: column;
  }
  .expiry-review-search {
    width: 100%;
  }
  .campaign-toolbar__filters,
  .campaign-toolbar__actions {
    width: 100%;
  }
  .campaign-toolbar__actions {
    margin-left: 0;
    justify-content: flex-start;
  }
  .status-filter,
  .search-field {
    max-width: none;
    width: 100%;
    min-width: 0;
  }
  .create-offer-meta {
    grid-template-columns: 1fr;
  }
  .campaign-table :deep(table) {
    min-width: 1280px;
  }
  .campaign-table :deep(thead th:nth-child(15)),
  .campaign-table :deep(tbody td:nth-child(15)) {
    width: 220px !important;
    min-width: 220px !important;
    max-width: 220px !important;
  }
  .campaign-categories-cell {
    width: 220px !important;
    max-width: 220px !important;
  }
  .carousel-crop-controls {
    grid-template-columns: minmax(0, 1fr) 104px;
    gap: 12px;
  }
}

@media (max-width: 1400px) {
  .campaign-toolbar__filters {
    flex-basis: 100%;
  }

  .campaign-toolbar__actions {
    width: 100%;
    margin-left: 0;
    justify-content: flex-start;
  }
}
</style>

<style>
.campaign-datetime-empty input {
  color: transparent !important;
  -webkit-text-fill-color: transparent !important;
}
.campaign-datetime-empty input::-webkit-datetime-edit,
.campaign-datetime-empty input::-webkit-datetime-edit-fields-wrapper,
.campaign-datetime-empty input::-webkit-datetime-edit-text,
.campaign-datetime-empty input::-webkit-datetime-edit-day-field,
.campaign-datetime-empty input::-webkit-datetime-edit-month-field,
.campaign-datetime-empty input::-webkit-datetime-edit-year-field,
.campaign-datetime-empty input::-webkit-datetime-edit-hour-field,
.campaign-datetime-empty input::-webkit-datetime-edit-minute-field,
.campaign-datetime-empty input::-webkit-datetime-edit-ampm-field {
  color: transparent !important;
  -webkit-text-fill-color: transparent !important;
}
.campaign-datetime-empty input::-webkit-calendar-picker-indicator {
  opacity: 0.72;
}
</style>
